import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, User, Loader2, ArrowLeft, Pencil, X } from "lucide-react";
import arnoldLogo from "@/assets/arnold-logo.png";
import arnoldAvatarGif from "@/assets/generated (1080 x 1080 px) (2).gif";
import arnoldSymbol from "@/assets/arnold-symbol.png";

// Check if Android (either native or web emulating Android)
const isAndroidPlatform = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

// Streaming configuration - optimized for smooth typewriter effect
// All platforms now use the same smooth animation approach
// Characters per second: ~400 (readable yet responsive)
const CHARS_PER_SECOND = 400;
// State update frequency: 60ms (balance between smoothness and performance)
const STATE_UPDATE_INTERVAL_MS = 60;
import { useState, useRef, useEffect, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useUserContext } from "@/hooks/useUserContext";
import { useProfile } from "@/hooks/useProfile";
import { isPremiumActive } from '@/lib/premiumFeatures';
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { getStraivClientPlatform } from "@/lib/clientPlatform";
import { getHealthConnectedState } from "@/lib/appleHealth";
import { buildHrZones, formatHeartRateSummaryForCoach } from "@/lib/heartRateZones";

// Platform check: true if running on web (not native iOS/Android)
const isWebPlatform = !Capacitor.isNativePlatform();
import { WorkoutImportModal } from "@/components/WorkoutImportModal";
import { SAFE_AREA } from "@/utils/safeArea";
import { WorkoutProgramReviewModal } from "@/components/WorkoutProgramReviewModal";
import { RecipeReviewModal } from "@/components/RecipeReviewModal";
import {
  parseWorkoutPlanFromMessage,
  convertToWorkoutLoggerFormat,
  importSingleWorkout,
  WorkoutPlan,
  RunningWorkoutPlan,
  formatRunningWorkoutForCoach,
  messageLikelyContainsWorkoutPlan,
} from "@/utils/workoutImporter";
import {
  appendArnoldRunPlans,
  CHAT_DEFER_CONTEXT_REFRESH_KEY,
  isRunningWorkoutCollection,
  isRunningWorkoutPlan,
  PENDING_CHAT_AUTO_SEND_KEY,
  syncArnoldRunPlansFromDetected,
} from "@/lib/runningCoachPlans";
import { Download, Calendar, Dumbbell, Play, CheckCircle2, Activity, ChefHat } from "lucide-react";
import { useSavedRecipes } from "@/hooks/useSavedRecipes";
import { Analytics } from "@/lib/analytics";
import { AiMessageMarkdown } from "@/components/chat/AiMessageMarkdown";
import { normalizeAiMarkdownForDisplay } from "@/lib/aiMarkdown";
import type { Json } from "@/integrations/supabase/types";


interface WorkoutProgramData {
  type: string;
  title: string;
  description: string;
  conversationId?: string;
  days: any[];
}

const COACH_CHAT_IMAGES_PER_DAY = 3;
/** Initial messages loaded when opening chat (keeps first paint fast). */
const CHAT_MESSAGES_INITIAL = 25;
/** Additional messages fetched when scrolling up. */
const CHAT_MESSAGES_LOAD_MORE = 25;

const activeConversationStorageKey = (userId: string) =>
  `straiv_active_conversation_${userId}`;

const persistActiveConversation = (userId: string, convId: string) => {
  try {
    localStorage.setItem(activeConversationStorageKey(userId), convId);
  } catch {
    /* ignore quota errors */
  }
};

const readActiveConversation = (userId: string): string | null => {
  try {
    return localStorage.getItem(activeConversationStorageKey(userId));
  } catch {
    return null;
  }
};

/** Keeps conversations.updated_at fresh when the DB trigger is not deployed yet. */
const touchConversationUpdatedAt = async (convId: string) => {
  try {
    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', convId);
  } catch (e) {
    console.warn('[Chat] failed to bump conversation updated_at:', e);
  }
};

const ARNOLD_LAST_RUN_KEY = 'arnoldLastRunSummary';

/** Tells Arnold whether Apple Health is linked; includes last run HR profile when available. */
const getCoachClientExtras = async (
  userId: string | undefined,
): Promise<{ clientHealthConnected: boolean; clientRecentRunSummary?: string }> => {
  if (!userId) return { clientHealthConnected: false };
  const connected = await getHealthConnectedState(userId).catch(() => false);
  let clientRecentRunSummary: string | undefined;
  try {
    const raw = localStorage.getItem(ARNOLD_LAST_RUN_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { userId?: string; coachText?: string; _ts?: number };
      const age = parsed._ts ? Date.now() - parsed._ts : Infinity;
      if (parsed.userId === userId && parsed.coachText && age < 14 * 24 * 60 * 60 * 1000) {
        clientRecentRunSummary = parsed.coachText;
      }
    }
  } catch {
    /* ignore */
  }
  return clientRecentRunSummary
    ? { clientHealthConnected: connected, clientRecentRunSummary }
    : { clientHealthConnected: connected };
};

interface ChatAttachment {
  url: string;
  mime_type?: string;
  name?: string;
}

interface Message {
  id: string;
  content: string;
  sender: "user" | "ai";
  timestamp: string;
  attachments?: ChatAttachment[];
  workoutPlan?: WorkoutPlan;
  workoutProgramData?: WorkoutProgramData;
  recipesData?: any[];
  failed?: boolean;
  retrying?: boolean;
}

// Memoized message component to prevent re-renders during typing
const MessageBubble = memo(({ 
  message, 
  detectedWorkoutPlans, 
  handleImportWorkoutPlan,
  handleImportWorkoutProgram,
  handleImportRunningWorkout,
  handleStartLater,
  handleShowRecipes,
  navigate,
  onQuickAction,
  isWelcomeMessage,
  onRetry,
  coachName,
  userAvatarUrl
}: { 
  message: Message;
  detectedWorkoutPlans: { [messageId: string]: WorkoutPlan };
  handleImportWorkoutPlan: (messageId: string) => void;
  handleImportWorkoutProgram: (messageId: string) => void;
  handleImportRunningWorkout: (messageId: string, workoutIndex?: number) => void;
  handleStartLater: (messageId: string) => void;
  handleShowRecipes: (recipes: any[]) => void;
  navigate: (path: string) => void;
  onQuickAction?: (prompt: string) => void;
  isWelcomeMessage?: boolean;
  onRetry?: (messageId: string) => void;
  coachName?: string;
  userAvatarUrl?: string | null;
}) => {
  const { t } = useTranslation();
  if (message.sender === "ai") {
    return (
      <div key={message.id} className="w-full mb-6 max-w-full overflow-x-hidden" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="flex items-center gap-3" style={{ marginBottom: '4px' }}>
          {/* Web: use arnold symbol, Native: use animated gif */}
          <img 
            src={isWebPlatform ? arnoldSymbol : arnoldAvatarGif} 
            alt="Arnold" 
            className="flex-shrink-0 rounded-lg" 
            style={{ height: '61px', width: '61px' }} 
          />
          <div className="font-medium text-muted-foreground" style={{ fontSize: '18px' }}>{coachName || 'Arnold'}</div>
        </div>
        <AiMessageMarkdown content={message.content} />

        {/* Quick action buttons for welcome message */}
        {isWelcomeMessage && onQuickAction && (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onQuickAction('single_workout')}
              className="flex items-center gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
            >
              <Dumbbell className="h-4 w-4 text-primary" />
              <span>{t('chat.quickActions.createWorkout')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onQuickAction('recipe')}
              className="flex items-center gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
            >
              <ChefHat className="h-4 w-4 text-primary" />
              <span>{t('chat.quickActions.createRecipe')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onQuickAction('weekly_program')}
              className="flex items-center gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
            >
              <Calendar className="h-4 w-4 text-primary" />
              <span>{t('chat.quickActions.weeklyProgram')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onQuickAction('rename_coach')}
              className="flex items-center gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors"
            >
              <Pencil className="h-4 w-4 text-primary" />
              <span>{t('chat.quickActions.renameCoach')}</span>
            </Button>
          </div>
        )}

        {message.workoutPlan && detectedWorkoutPlans[message.id] && (
          <div className="mt-2">
            <Card className="border-primary/20 bg-gradient-card backdrop-blur-sm">
              <CardContent className="p-3 sm:p-4">
                {detectedWorkoutPlans[message.id].type === 'single_workout' ? (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Dumbbell className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium text-primary">{t('chat.cards.singleWorkoutPlan')}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('chat.cards.exercisesCount', { count: (detectedWorkoutPlans[message.id] as any).exercises?.length || 0 })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleImportWorkoutPlan(message.id)}
                          className="bg-gradient-primary text-black hover:shadow-md"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          {t('chat.cards.startNow')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStartLater(message.id)}
                          className="border-border/60 hover:bg-secondary/10"
                        >
                          {t('chat.cards.startLater')}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 mt-3 border-t pt-3">
                      {(detectedWorkoutPlans[message.id] as any).exercises?.map((exercise: any, idx: number) => (
                        <div key={idx} className="bg-muted/50 rounded-lg p-3">
                          <div className="font-medium text-sm mb-1">{exercise.exercise_name}</div>
                          <div className="text-xs text-muted-foreground space-y-1">
                            <div>
                              {t('chat.cards.setsByReps', { sets: exercise.sets, reps: exercise.reps })}
                              {exercise.rest_seconds ? ` • ${t('chat.cards.rest', { seconds: exercise.rest_seconds })}` : ''}
                            </div>
                            {(exercise.weight_kg !== null && exercise.weight_kg !== undefined && exercise.weight_kg !== '' && Number(exercise.weight_kg) > 0) && (
                              <div className="text-primary font-medium">
                                {Number(exercise.weight_kg)} kg
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : detectedWorkoutPlans[message.id].type === 'running_workout' ? (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium text-primary">{(detectedWorkoutPlans[message.id] as any).title || 'Running workout ready'}</p>
                        <p className="text-xs text-muted-foreground">Import to AI run guide</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleImportRunningWorkout(message.id)}
                      className="bg-gradient-primary text-black hover:shadow-md"
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Use in Run
                    </Button>
                  </div>
                ) : detectedWorkoutPlans[message.id].type === 'running_workouts' ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium text-primary">Running workouts ready</p>
                        <p className="text-xs text-muted-foreground">
                          {(detectedWorkoutPlans[message.id] as any).workouts?.length || 0} options for AI run guide
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {((detectedWorkoutPlans[message.id] as any).workouts || []).map((run: RunningWorkoutPlan, idx: number) => (
                        <div key={idx} className="bg-muted/50 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{run.title || `Run workout ${idx + 1}`}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2">{formatRunningWorkoutForCoach(run)}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleImportRunningWorkout(message.id, idx)}
                              className="shrink-0 bg-gradient-primary text-black"
                            >
                              Use
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : detectedWorkoutPlans[message.id].type === 'weekly_plan' ? (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium text-primary">{t('chat.cards.weeklyPlanDetected')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('chat.cards.daysOfWorkouts', { count: (detectedWorkoutPlans[message.id] as any).days?.length || 0 })}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleImportWorkoutPlan(message.id)}
                      className="bg-gradient-primary text-black hover:shadow-md"
                    >
                      <Play className="h-3 w-3 mr-1" />
                      {t('chat.cards.viewPlan')}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}

        {message.workoutProgramData && (
          <div className="mt-2">
            <Card className="border-primary/20 bg-gradient-card backdrop-blur-sm">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-primary">{t('chat.cards.workoutProgramReady')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('chat.cards.daysOfWorkouts', { count: message.workoutProgramData.days.length })}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleImportWorkoutProgram(message.id)}
                    className="bg-gradient-primary text-black hover:shadow-md"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {t('chat.cards.reviewAndImport')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {message.recipesData && Array.isArray(message.recipesData) && message.recipesData.length > 0 && (
          <div className="mt-2">
            <Card className="border-primary/20 bg-gradient-card backdrop-blur-sm">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ChefHat className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-primary">{message.recipesData.length > 1 ? t('chat.cards.recipesSuggested') : t('chat.cards.recipeSuggested')}</p>
                      <p className="text-xs text-muted-foreground">
                        {message.recipesData.length} recipe{message.recipesData.length > 1 ? 's' : ''} ready to review
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleShowRecipes(message.recipesData)}
                    className="bg-gradient-primary text-black hover:shadow-md"
                  >
                    <ChefHat className="h-3 w-3 mr-1" />
                    View Recipe{message.recipesData.length > 1 ? 's' : ''}
                  </Button>
                </div>
                <div className="space-y-2 mt-3 border-t pt-3">
                  {message.recipesData.slice(0, 3).map((recipe: any, idx: number) => (
                    <div key={idx} className="bg-muted/50 rounded-lg p-3">
                      <div className="font-medium text-sm mb-1">{recipe.title || `Recipe ${idx + 1}`}</div>
                      {recipe.description && (
                        <div className="text-xs text-muted-foreground line-clamp-2 mb-2">
                          {recipe.description}
                        </div>
                      )}
                      {(recipe.calories || recipe.protein || recipe.carbs || recipe.fat) && (
                        <div className="text-xs text-muted-foreground">
                          {recipe.calories && `${recipe.calories} cal`}
                          {recipe.protein && ` • ${recipe.protein}g protein`}
                          {recipe.carbs && ` • ${recipe.carbs}g carbs`}
                          {recipe.fat && ` • ${recipe.fat}g fat`}
                        </div>
                      )}
                    </div>
                  ))}
                  {message.recipesData.length > 3 && (
                    <div className="text-xs text-muted-foreground text-center pt-2">
                      +{message.recipesData.length - 3} more recipe{message.recipesData.length - 3 > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        <p className="text-xs mt-3 text-muted-foreground">
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    );
  }
  
  // User message bubble - iOS WebView compatible
  return (
    <div key={message.id} className="flex gap-3 justify-end mb-4">
      <div className="max-w-[80%] order-2">
        <div className={`p-4 rounded-2xl ${message.failed ? 'bg-destructive/80' : 'bg-gradient-chat'} text-white`}>
          {message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {message.attachments.map((att, idx) => (
                <a
                  key={`${att.url}-${idx}`}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg overflow-hidden border border-white/20 bg-black/20 shrink-0"
                >
                  <img
                    src={att.url}
                    alt=""
                    className="max-h-28 max-w-[120px] object-cover"
                  />
                </a>
              ))}
            </div>
          )}
          {message.content?.trim() ? (
            <p 
              className="whitespace-pre-wrap text-sm sm:text-base leading-relaxed break-words"
              style={{
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                fontWeight: 400,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              } as React.CSSProperties}
            >
              {message.content}
            </p>
          ) : message.attachments?.length ? (
            <p className="text-sm text-white/90 italic">{t('chat.attachments.imageOnlyCaption')}</p>
          ) : null}
          {message.failed && !message.retrying && onRetry && (
            <div className="mt-2 pt-2 border-t border-white/20">
              <button
                onClick={() => onRetry(message.id)}
                className="flex items-center gap-1.5 text-xs text-white/90 hover:text-white transition-colors"
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                </svg>
                Retry
              </button>
            </div>
          )}
          {message.retrying && (
            <div className="mt-2 pt-2 border-t border-white/20 flex items-center gap-1.5 text-xs text-white/70">
              <Loader2 className="h-3 w-3 animate-spin" />
              Retrying...
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-2">
          {message.failed && !message.retrying && (
            <span className="text-xs text-destructive">{t('chat.failedToSend')}</span>
          )}
          <p className="text-xs text-white/70">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-secondary/20 flex items-center justify-center">
        {userAvatarUrl ? (
          <img src={userAvatarUrl} alt="You" className="w-full h-full object-cover" />
        ) : (
          <User className="h-4 w-4 text-secondary" />
        )}
      </div>
    </div>
  );
});

export const Chat = () => {
  const { user } = useAuth();
  const { context, getContextSummary, refetch: refetchContext } = useUserContext();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { dailyNutrition, nutritionGoals } = useComprehensiveNutrition();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  /**
   * Locale we send to ai-coach-chat so the AI replies in the user's chosen
   * language. Pulled from i18next (which is already wired to the device
   * default + Settings picker) so this stays in sync with the rest of the UI.
   */
  const clientLocale = i18n.language;
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Generate personalized welcome message - warm, engaging, coach-like
  const generateWelcomeMessage = () => {
    if (!profile) {
      const name = profile?.ai_coach_name || 'Arnold';
      return `## Hey, I'm ${name}!\n\nI'm your AI fitness coach, and I'm here to help you get stronger, eat better, and crush your goals.\n\n**What I can help you with:**\n\n- **Workouts** – Custom training plans tailored to your level and goals\n- **Nutrition** – Meal ideas and tracking to fuel your progress\n- **Progress** – Analyzing your lifts and spotting what's working\n- **Motivation** – Keeping you accountable when you need a push\n\nSo, what's on your mind today?"`;
    }

    const firstName = profile.first_name || "there";
    const primaryGoals = profile.primary_goals || [];
    const specificGoals = profile.specific_goals || "";
    const experienceYears = profile.experience_years;

    let message = `## Hey ${firstName}!\n\n`;
    const coachName = profile.ai_coach_name || 'Arnold';
    message += `Good to see you. I'm **${coachName}**, your AI coach.\n\n`;
    
    // Add personalized context based on experience
    if (experienceYears && experienceYears > 0) {
      message += `With **${experienceYears} year${experienceYears > 1 ? 's' : ''}** of training under your belt, you've already built a solid foundation. `;
    }
    
    // Prioritize specific goals - they're the real targets
    if (specificGoals && specificGoals.trim()) {
      message += `I know you're chasing **${specificGoals}** – that's what we're building towards. `;
      if (primaryGoals.length > 0) {
        const formattedGoals = primaryGoals.map(g => g.toLowerCase()).join(" and ");
        message += `Every session is about ${formattedGoals} to get you there.\n\n`;
      } else {
        message += "Let's make it happen.\n\n";
      }
    } else if (primaryGoals.length > 0) {
      const formattedGoals = primaryGoals.map(g => `**${g.toLowerCase()}**`).join(" and ");
      message += `I know you're focused on ${formattedGoals}. Let's make it happen.\n\n`;
    } else {
      message += "I'm here to help you reach your fitness goals.\n\n";
    }
    
    message += "**What I can help you with:**\n\n";
    message += "- **Workouts** – Custom training plans based on your history\n";
    message += "- **Nutrition** – Smart fueling for recovery and performance\n";
    message += "- **Progress** – Breaking down your lifts and tracking gains\n";
    message += "- **Recovery** – Sleep, rest days, and staying injury-free\n\n";
    
    message += "What would you like to work on?";

    return message;
  };
  
  const [messages, setMessages] = useState<Message[]>([]);
  // Remove controlled state for input - use ref only for instant typing
  const [isLoading, setIsLoading] = useState(false);
  
  // iOS keyboard height tracking for input positioning
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Track input value in ref ONLY - no state updates during typing
  // This completely eliminates the 2-3 second delay on Android WebViews
  const inputValueRef = useRef("");
  
  // Track if send button should be enabled (separate from input value)
  const [canSend, setCanSend] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<{ file: File; preview: string }[]>([]);
  const [coachImagesUsedToday, setCoachImagesUsedToday] = useState(0);
  
  // Optimized input handler - refs only, no state during typing
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Update ref immediately - this is instant
    inputValueRef.current = value;
    // Only update canSend state (minimal re-render) with debounce
    const hasContent = value.trim().length > 0 || pendingAttachments.length > 0;
    if (hasContent !== canSend) {
      setCanSend(hasContent);
    }
  }, [canSend, pendingAttachments.length]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingOlderHistory, setLoadingOlderHistory] = useState(false);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [detectedWorkoutPlans, setDetectedWorkoutPlans] = useState<{ [messageId: string]: WorkoutPlan }>({});
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [selectedProgramData, setSelectedProgramData] = useState<WorkoutProgramData | null>(null);
  const [isImportingProgram, setIsImportingProgram] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState<any[]>([]);
  const [currentRecipeIndex, setCurrentRecipeIndex] = useState(0);
  const [isImportingRecipe, setIsImportingRecipe] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatImageInputRef = useRef<HTMLInputElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  // Keyboard handling done via CSS
  // Track processed workout analysis keys to prevent duplicates in this session
  const processedAnalysisKeysRef = useRef<Set<string>>(new Set());

  const refreshCoachImageUsage = useCallback(async () => {
    if (!user?.id) return;
    const periodKey = `day:${new Date().toLocaleDateString('en-CA')}`;
    const { data, error } = await (supabase as any)
      .from('ai_usage_counters')
      .select('usage_count')
      .eq('user_id', user.id)
      .eq('feature', 'coach_chat_image')
      .eq('period_key', periodKey)
      .maybeSingle();
    if (!error) setCoachImagesUsedToday(Number(data?.usage_count ?? 0));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !messagesLoaded) return;
    void refreshCoachImageUsage();
  }, [user?.id, messagesLoaded, refreshCoachImageUsage]);

  const removePendingAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      const hasText = inputValueRef.current.trim().length > 0;
      setCanSend(hasText || next.length > 0);
      return next;
    });
  }, []);

  const onCoachChatFilesSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    e.target.value = '';
    if (!list?.length || !user?.id) return;
    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
    const picked = Array.from(list).filter((f) => allowed.has(f.type));
    if (!picked.length) {
      toast.error(t('chat.attachments.invalidType'));
      return;
    }
    if (picked.length < list.length) {
      toast.error(t('chat.attachments.invalidType'));
    }

    setPendingAttachments((prev) => {
      const availableSlots = Math.max(
        0,
        COACH_CHAT_IMAGES_PER_DAY - coachImagesUsedToday - prev.length,
      );
      if (availableSlots <= 0) {
        setTimeout(() => toast.error(t('chat.attachments.dailyLimitReached')), 0);
        return prev;
      }
      const toAdd = picked.slice(0, availableSlots);
      if (toAdd.length < picked.length) {
        setTimeout(() => toast.info(t('chat.attachments.partialAdd')), 0);
      }
      const next = [...prev];
      for (const file of toAdd) {
        next.push({ file, preview: URL.createObjectURL(file) });
      }
      const hasText = inputValueRef.current.trim().length > 0;
      setTimeout(() => setCanSend(hasText || next.length > 0), 0);
      return next;
    });
  }, [user?.id, coachImagesUsedToday, t]);

  const openCoachChatImagePicker = useCallback(async () => {
    const availableSlots = COACH_CHAT_IMAGES_PER_DAY - coachImagesUsedToday - pendingAttachments.length;
    if (availableSlots <= 0) {
      toast.error(t('chat.attachments.dailyLimitReached'));
      return;
    }

    // On native (iOS/Android) the WKWebView <input type="file"> picker is
    // unreliable - it sometimes silently fails to return a file or asks for
    // permission that the embedded page never gets. Use the Capacitor Camera
    // plugin instead, which goes through the proper PHPicker / photo picker.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await Camera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Photos,
          quality: 85,
          allowEditing: false,
          saveToGallery: false,
          correctOrientation: true,
          presentationStyle: 'fullscreen',
        });
        const dataUrl = photo.dataUrl;
        if (!dataUrl) return;
        const resp = await fetch(dataUrl);
        const blob = await resp.blob();
        const mime = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
        const file = new File([blob], `photo-${Date.now()}.${ext}`, { type: mime });

        setPendingAttachments((prev) => {
          if (COACH_CHAT_IMAGES_PER_DAY - coachImagesUsedToday - prev.length <= 0) {
            setTimeout(() => toast.error(t('chat.attachments.dailyLimitReached')), 0);
            return prev;
          }
          const next = [...prev, { file, preview: URL.createObjectURL(file) }];
          const hasText = inputValueRef.current.trim().length > 0;
          setTimeout(() => setCanSend(hasText || next.length > 0), 0);
          return next;
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/cancel/i.test(msg)) return; // user dismissed the picker
        console.warn('[CoachChat] native picker failed, falling back to file input', err);
        chatImageInputRef.current?.click();
      }
      return;
    }

    chatImageInputRef.current?.click();
  }, [coachImagesUsedToday, pendingAttachments.length, t]);

  const { createRecipe } = useSavedRecipes();

  // Keep a ref for detected plans to stabilize callbacks
  const detectedPlansRef = useRef(detectedWorkoutPlans);
  useEffect(() => {
    detectedPlansRef.current = detectedWorkoutPlans;
  }, [detectedWorkoutPlans]);

  const persistDetectedRunningPlans = useCallback((plan: WorkoutPlan, suggestedAt?: string) => {
    try {
      const at = suggestedAt || new Date().toISOString();
      if (isRunningWorkoutPlan(plan)) appendArnoldRunPlans([{ plan, suggestedAt: at }]);
      else if (isRunningWorkoutCollection(plan)) {
        appendArnoldRunPlans(plan.workouts.map((workout) => ({ plan: workout, suggestedAt: at })));
      }
    } catch (err) {
      console.warn('Failed to persist Arnold running plan:', err);
    }
  }, []);

  // Dismiss keyboard by blurring active element
  const dismissKeyboard = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Native keyboard height tracking - listen to native keyboard events
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const isIOS = Capacitor.getPlatform() === 'ios';
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';

    let showListener: any;
    let hideListener: any;

    const setupKeyboardListeners = async () => {
      try {
        showListener = await Keyboard.addListener(showEvent as any, (info: any) => {
          setKeyboardHeight(info.keyboardHeight);
          setTimeout(() => {
            if (scrollAreaRef.current) {
              scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
            }
          }, 100);
        });

        hideListener = await Keyboard.addListener(hideEvent as any, () => {
          setKeyboardHeight(0);
        });
      } catch (error) {
        console.warn('[Chat] Keyboard listener setup failed:', error);
      }
    };

    setupKeyboardListeners();

    return () => {
      showListener?.remove();
      hideListener?.remove();
    };
  }, []);

  // Track if user has manually scrolled up (to prevent auto-scroll when they're reading)
  const userScrolledUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const userTouchingRef = useRef(false);
  const touchStartYRef = useRef(0);
  const isStreamingRef = useRef(false);
  const loadingOlderHistoryRef = useRef(false);

  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || loadingOlderHistoryRef.current) return;
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const chronological = messages.filter((m) => m.id !== 'welcome');
    const oldest = chronological[0];
    if (!oldest?.timestamp) return;

    loadingOlderHistoryRef.current = true;
    setLoadingOlderHistory(true);
    const prevScrollHeight = scrollArea.scrollHeight;

    try {
      const { data: olderRows, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .lt('created_at', oldest.timestamp)
        .order('created_at', { ascending: false })
        .limit(CHAT_MESSAGES_LOAD_MORE);

      if (error) throw error;
      const olderBatch = (olderRows || []).slice().reverse();
      setHasMoreHistory(olderBatch.length >= CHAT_MESSAGES_LOAD_MORE);
      if (olderBatch.length === 0) return;

      const olderMessages: Message[] = olderBatch.map((msg: any) => ({
        id: msg.id,
        content: msg.sender === 'ai' ? formatAiText(msg.content) : (msg.content ?? ''),
        sender: msg.sender as 'user' | 'ai',
        timestamp: msg.created_at,
        attachments: Array.isArray(msg.attachments)
          ? (msg.attachments as ChatAttachment[])
          : undefined,
      }));

      setMessages((prev) => {
        const welcome = prev.find((m) => m.id === 'welcome');
        const rest = prev.filter((m) => m.id !== 'welcome');
        const merged = [...olderMessages, ...rest];
        return welcome ? [welcome, ...merged] : merged;
      });

      requestAnimationFrame(() => {
        if (!scrollAreaRef.current) return;
        const next = scrollAreaRef.current;
        next.scrollTop = next.scrollHeight - prevScrollHeight;
      });
    } catch (err) {
      console.warn('[Chat] Failed to load older messages:', err);
    } finally {
      loadingOlderHistoryRef.current = false;
      setLoadingOlderHistory(false);
    }
  }, [conversationId, messages]);

  // Handle scroll events to detect when user manually scrolls
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const handleScroll = () => {
      if (!scrollArea) return;
      
      const scrollTop = scrollArea.scrollTop;
      const scrollHeight = scrollArea.scrollHeight;
      const clientHeight = scrollArea.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (scrollTop < 80 && hasMoreHistory && !loadingOlderHistoryRef.current) {
        void loadOlderMessages();
      }
      
      // If user is actively touching/scrolling, check if they scrolled up
      if (userTouchingRef.current) {
        // If user scrolled up more than 50px from bottom, pause auto-scroll
        if (distanceFromBottom > 50) {
          userScrolledUpRef.current = true;
        } else if (distanceFromBottom < 20) {
          // If user scrolls back near bottom, resume auto-scroll
          userScrolledUpRef.current = false;
        }
      }
      
      lastScrollTopRef.current = scrollTop;
    };

    // Track touch/scroll start to detect user interaction
    const handleTouchStart = (e: TouchEvent) => {
      userTouchingRef.current = true;
      touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      // Keep touching flag for a short time to catch scroll momentum
      setTimeout(() => {
        userTouchingRef.current = false;
      }, 300);
    };

    // Also track mouse events for desktop
    const handleMouseDown = () => {
      userTouchingRef.current = true;
    };

    const handleMouseUp = () => {
      setTimeout(() => {
        userTouchingRef.current = false;
      }, 300);
    };

    scrollArea.addEventListener('scroll', handleScroll, { passive: true });
    scrollArea.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollArea.addEventListener('touchend', handleTouchEnd, { passive: true });
    scrollArea.addEventListener('mousedown', handleMouseDown, { passive: true });
    scrollArea.addEventListener('mouseup', handleMouseUp, { passive: true });
    
    return () => {
      scrollArea.removeEventListener('scroll', handleScroll);
      scrollArea.removeEventListener('touchstart', handleTouchStart);
      scrollArea.removeEventListener('touchend', handleTouchEnd);
      scrollArea.removeEventListener('mousedown', handleMouseDown);
      scrollArea.removeEventListener('mouseup', handleMouseUp);
    };
  }, [hasMoreHistory, loadOlderMessages]);

      // Scroll to top function - for showing welcome message
      const scrollToTop = () => {
        if (!scrollAreaRef.current) return;
        scrollAreaRef.current.scrollTop = 0;
        lastScrollTopRef.current = 0;
        userScrolledUpRef.current = false;
      };

      // Scroll to bottom function - Cursor-like behavior: ALWAYS follow text during streaming
      // Text generation NEVER pauses - only auto-scroll pauses if user is actively scrolling up
      const scrollToBottom = (force = false) => {
        if (!scrollAreaRef.current) return;
        
        const scrollArea = scrollAreaRef.current;
        const scrollTop = scrollArea.scrollTop;
        const scrollHeight = scrollArea.scrollHeight;
        const clientHeight = scrollArea.clientHeight;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        // During streaming: ALWAYS scroll to show latest text unless user is actively scrolling up
        // Text generation continues regardless - we just control whether to auto-scroll
        if (isStreamingRef.current) {
          // If user is actively touching/scrolling AND has scrolled up, don't auto-scroll
          if (userTouchingRef.current && userScrolledUpRef.current) {
            // User is actively scrolling up - don't auto-scroll, but text generation continues
            return;
          }
          // User is not touching OR is near bottom - always follow the text
          scrollArea.scrollTop = scrollArea.scrollHeight;
          lastScrollTopRef.current = scrollArea.scrollTop;
          // Only reset flag if user is not actively touching
          if (!userTouchingRef.current) {
            userScrolledUpRef.current = false;
          }
          return;
        }
        
        // After streaming: only scroll if user is at bottom or force is true
        const isAtBottom = distanceFromBottom < 100;
        if (isAtBottom || force) {
          scrollArea.scrollTop = scrollArea.scrollHeight;
          if (isAtBottom) {
            userScrolledUpRef.current = false;
          }
          lastScrollTopRef.current = scrollArea.scrollTop;
        }
      };

  // Normalize and compact AI Markdown for clean structure + enforce strict spacing (two empty lines between paragraphs)
  const formatAiText = (text: string): string => {
    // Use the same normalization function for consistency
    let s = normalizeAiMarkdownForDisplay(String(text ?? ''));
    
    // CRITICAL: Remove date/time mentions that Arnold shouldn't include
    // Remove patterns like "Today is Tuesday, 2025-12-23. The current UTC time is..."
    s = s.replace(/^Today is [^.]*\.\s*The current UTC time is [^.]*\.\s*/i, '');
    s = s.replace(/Today is [^.]*\.\s*The current UTC time is [^.]*\.\s*/gi, '');
    s = s.replace(/The current UTC time is [^.]*\.\s*/gi, '');
    s = s.replace(/Today is [^.]*\.\s*/gi, '');
    s = s.replace(/\(Today is [^)]*\)/gi, '');
    s = s.replace(/\(The current UTC time is [^)]*\)/gi, '');
    s = s.trim();

    // Remove bracketed section headers
    s = s.replace(/^\s*\[(?:INTRO|MAIN ADVICE|ACTION STEPS|MOTIVATION)\]\s*/gm, '');
    s = s.replace(/^\s*\[(?:INTRO|MAIN ADVICE|ACTION STEPS|MOTIVATION)\]\s*$/gm, '');
    s = s.replace(/\[(?:TIP|WARNING|NOTE)\]\s*/g, '');

    // Normalize line endings but PRESERVE double newlines (ReactMarkdown needs them)
    s = s.replace(/\r\n/g, '\n');
    s = s.replace(/\r/g, '\n');
    
    // Normalize bullet styles (ReactMarkdown handles both, but let's standardize)
    s = s.replace(/^\s*[•–—]\s+/gm, '- '); // Unicode bullets -> '- '

    // Collapse excessive newlines (3+ -> 2)
    s = s.replace(/(\n\s*){3,}/g, '\n\n');
    
    // Ensure headings have proper spacing (ReactMarkdown needs blank lines)
    s = s.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');
    s = s.replace(/(#{1,6}.*?)\n(?!\n)/g, '$1\n\n');

    // Ensure lists have proper spacing (blank line before list)
    s = s.replace(/([^\n])\n(?=(- |\d+\. ))/g, '$1\n\n');

    // Trim trailing spaces
    s = s.replace(/[\t ]+$/gm, '');
    s = s.trim();

    return s;
  };
  // Reset load flag on unmount so returning to Chat always refetches history.
  useEffect(() => {
    return () => {
      setMessagesLoaded(false);
    };
  }, []);

  const adoptConversationId = useCallback(
    (convId: string | null) => {
      if (!convId) return;
      setConversationId(convId);
      if (user?.id) persistActiveConversation(user.id, convId);
    },
    [user?.id],
  );

  // Refresh context when Chat page loads to ensure latest profile data
  useEffect(() => {
    if (user && !profileLoading) {
      console.log('Chat page mounted, forcing context refresh...');
      Analytics.chatOpened();
      const deferRefresh = sessionStorage.getItem(CHAT_DEFER_CONTEXT_REFRESH_KEY) === '1';
      if (deferRefresh) {
        sessionStorage.removeItem(CHAT_DEFER_CONTEXT_REFRESH_KEY);
        const timer = window.setTimeout(() => refetchContext(true), 4000);
        return () => window.clearTimeout(timer);
      }
      refetchContext(true);
    }
  }, [user, profileLoading]);

  // Removed immediate pending workout pickup to avoid double-processing. The navigation-state handler below (with a localStorage fallback) will handle this safely.

  // Real-time subscription for nutrition and workout updates
  useEffect(() => {
    if (!user) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleContextRefresh = (reason: string) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        console.log(`${reason} — debounced Arnold context refresh`);
        refetchContext(true);
      }, 2500);
    };

    const channel = supabase
      .channel('arnold-context-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'food_entries'
        },
        () => scheduleContextRefresh('Food entry changed')
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meals',
          filter: `user_id=eq.${user.id}`
        },
        () => scheduleContextRefresh('Meal changed')
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'nutrition_logs',
          filter: `user_id=eq.${user.id}`
        },
        () => scheduleContextRefresh('Nutrition log changed')
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workouts',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new && payload.new.completed_at) {
            scheduleContextRefresh('Workout completed');
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sleep_logs',
          filter: `user_id=eq.${user.id}`
        },
        () => scheduleContextRefresh('Sleep log changed')
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [user, refetchContext]);

  // Load existing conversation and messages
  useEffect(() => {
    const loadExistingMessages = async () => {
      if (!user || messagesLoaded || profileLoading) return; // Wait for profile to load

      try {
        // Resolve which conversation to open:
        // 1) last active (localStorage)  2) globally newest message  3) newest row
        let targetConvId: string | null = null;

        const storedConvId = readActiveConversation(user.id);
        if (storedConvId) {
          const { data: storedConv } = await supabase
            .from('conversations')
            .select('id')
            .eq('id', storedConvId)
            .eq('user_id', user.id)
            .maybeSingle();
          if (storedConv?.id) targetConvId = storedConv.id;
        }

        if (!targetConvId) {
          const { data: convRows, error: convListErr } = await supabase
            .from('conversations')
            .select('id')
            .eq('user_id', user.id);
          if (convListErr) throw convListErr;

          const convIds = (convRows || []).map((c: { id: string }) => c.id);
          if (convIds.length > 0) {
            const { data: latestMsg, error: latestErr } = await supabase
              .from('messages')
              .select('conversation_id, created_at')
              .in('conversation_id', convIds)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (latestErr) throw latestErr;
            if (latestMsg?.conversation_id) targetConvId = latestMsg.conversation_id;
          }
        }

        if (!targetConvId) {
          const { data: fallbackConv, error: fallbackErr } = await supabase
            .from('conversations')
            .select('id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (fallbackErr) throw fallbackErr;
          if (fallbackConv?.id) targetConvId = fallbackConv.id;
        }

        if (targetConvId) {
          adoptConversationId(targetConvId);
          console.log('[Chat] Loading conversation', targetConvId);

          // Load only the latest page of messages (newest first, then reverse).
          const { data: recentMessages, error: msgError } = await supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', targetConvId)
            .order('created_at', { ascending: false })
            .limit(CHAT_MESSAGES_INITIAL);

          if (msgError) throw msgError;

          const conversationMessages = (recentMessages || []).slice().reverse();
          console.log(
            `[Chat] Loaded ${conversationMessages.length} message(s) (latest ${CHAT_MESSAGES_INITIAL})`,
          );

          if (conversationMessages.length > 0) {
            setHasMoreHistory(conversationMessages.length >= CHAT_MESSAGES_INITIAL);

            const welcomeMessage: Message = {
              id: "welcome",
              content: generateWelcomeMessage(),
              sender: "ai",
              timestamp: new Date(0).toISOString(),
            };

            const quickMessages: Message[] = conversationMessages.map((msg: any) => ({
              id: msg.id,
              content: (msg.sender === 'ai') ? formatAiText(msg.content) : (msg.content ?? ''),
              sender: msg.sender as "user" | "ai",
              timestamp: msg.created_at,
              attachments: Array.isArray(msg.attachments)
                ? (msg.attachments as ChatAttachment[])
                : undefined,
            }));

            setMessages(() => {
              const filteredLoaded = quickMessages.filter(m => m.id !== 'welcome');
              return [welcomeMessage, ...filteredLoaded];
            });
            setMessagesLoaded(true);

            // Enrich with workout templates/programs in the background (doesn't block first paint).
            void (async () => {
            const messageIds = conversationMessages.map((m: any) => m.id);
            console.log(`Loading workout templates for ${messageIds.length} message(s)`);

            const MESSAGE_ID_CHUNK = 100;
            const templates: any[] = [];
            for (let i = 0; i < messageIds.length; i += MESSAGE_ID_CHUNK) {
              const chunk = messageIds.slice(i, i + MESSAGE_ID_CHUNK);
              const { data: chunkRows, error: tmplChunkErr } = await supabase
                .from('workout_templates')
                .select('id, original_message_id, exercises')
                .in('original_message_id', chunk)
                .eq('user_id', user.id);
              if (tmplChunkErr) throw tmplChunkErr;
              if (chunkRows?.length) templates.push(...chunkRows);
            }

            console.log('Found workout templates:', templates.length);

            const { data: programs } = await supabase
              .from('workout_programs')
              .select('*, workout_program_days(*, workout_program_exercises(*))')
              .eq('conversation_id', targetConvId)
              .eq('user_id', user.id);

            console.log('Found workout programs:', programs?.length || 0);

            const byMessageId: Record<string, WorkoutPlan> = {};
            (templates || []).forEach((t: any) => {
              if (t.original_message_id && t.exercises) {
                byMessageId[t.original_message_id] = {
                  type: 'single_workout',
                  exercises: t.exercises,
                } as WorkoutPlan;
              }
            });

            // Map programs by message_id
            const programsByMessageId: Record<string, any> = {};
            (programs || []).forEach((p: any) => {
              if (p.message_id && p.created_from_chat) {
                programsByMessageId[p.message_id] = {
                  type: p.program_type || 'weekly_program',
                  title: p.title,
                  description: p.description,
                  conversationId: p.conversation_id,
                  days: (p.workout_program_days || []).map((day: any) => ({
                    ...day,
                    exercises: day.workout_program_exercises || []
                  }))
                };
              }
            });

            const parseRecipesFromContent = (content: string): any[] | null => {
              try {
                const recipeMatch = content.match(/```json\s*(\[\s*\{[\s\S]*?"title"[\s\S]*?"ingredients"[\s\S]*?\}[\s\S]*?\])\s*```/);
                if (recipeMatch) {
                  const parsed = JSON.parse(recipeMatch[1]);
                  if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].title && parsed[0].ingredients) {
                    return parsed;
                  }
                }
              } catch {
                /* not a recipe message */
              }
              return null;
            };

            const loadedMessages: Message[] = conversationMessages.map((msg: any) => {
              let parsedPlan: WorkoutPlan | null = byMessageId[msg.id] || null;
              if (!parsedPlan && msg.sender === 'ai' && messageLikelyContainsWorkoutPlan(msg.content)) {
                parsedPlan = parseWorkoutPlanFromMessage(msg.content);
              }
              if (parsedPlan) {
                byMessageId[msg.id] = parsedPlan;
              }

              const programData = msg.sender === 'ai' ? programsByMessageId[msg.id] : null;
              const recipesData = msg.sender === 'ai' ? parseRecipesFromContent(msg.content) : null;

              return {
                id: msg.id,
                content: (msg.sender === 'ai') ? formatAiText(msg.content) : (msg.content ?? ''),
                sender: msg.sender as "user" | "ai",
                timestamp: msg.created_at,
                attachments: Array.isArray(msg.attachments)
                  ? (msg.attachments as ChatAttachment[])
                  : undefined,
                workoutPlan: parsedPlan || undefined,
                workoutProgramData: programData || undefined,
                recipesData: recipesData || undefined,
              };
            });

            console.log('Total workout plans detected:', Object.keys(byMessageId).length);
            setDetectedWorkoutPlans(byMessageId);
            try {
              const messageTimestamps = Object.fromEntries(
                conversationMessages.map((msg: { id: string; created_at?: string }) => [
                  msg.id,
                  msg.created_at || '',
                ]),
              );
              syncArnoldRunPlansFromDetected(byMessageId, messageTimestamps);
            } catch (syncErr) {
              console.warn('Failed to sync Arnold run plans from chat history:', syncErr);
            }
            Object.keys(byMessageId).forEach(messageId => {
              const plan = byMessageId[messageId];
              if (plan.type === 'single_workout') {
                Analytics.arnoldWorkoutSuggested();
              } else if (plan.type === 'weekly_plan') {
                Analytics.arnoldProgramGenerated();
              }
            });

            setMessages((prev) => {
              const welcome = prev.find((m) => m.id === 'welcome');
              const enrichedById = new Map(loadedMessages.map((m) => [m.id, m]));
              const merged = prev
                .filter((m) => m.id !== 'welcome')
                .map((m) => enrichedById.get(m.id) ?? m);
              return welcome ? [welcome, ...merged] : merged;
            });
            })();
            return;
          } else {
            // No messages - just show welcome message
            setMessages([{
              id: "welcome",
              content: generateWelcomeMessage(),
              sender: "ai",
              timestamp: new Date().toISOString(),
            }]);
          }
        } else {
          // No conversations found - show welcome message
          setMessages([{
            id: "welcome",
            content: generateWelcomeMessage(),
            sender: "ai",
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        // Show welcome message on error
        setMessages([{
          id: "welcome",
          content: generateWelcomeMessage(),
          sender: "ai",
          timestamp: new Date().toISOString(),
        }]);
      } finally {
        setMessagesLoaded(true);
        // Scroll behavior will be handled by useEffect that watches messagesLoaded
      }
    };

    loadExistingMessages();
  }, [user, messagesLoaded, profileLoading, profile, adoptConversationId]);

  // Handle goal analysis or workout analysis request from navigation state
  useEffect(() => {
    if (!messagesLoaded || !user) return;
    
    const prefilledMessage = location.state?.prefilledMessage;
    const goalContext = location.state?.goalContext;
    const workoutData = location.state?.workoutData;
    const fromWorkout = location.state?.fromWorkout;
    let simpleMessage = location.state?.message as string | undefined;
    if (!simpleMessage) {
      try {
        simpleMessage = sessionStorage.getItem(PENDING_CHAT_AUTO_SEND_KEY) || undefined;
      } catch {
        simpleMessage = undefined;
      }
    }

    console.log('Navigation state check:', { 
      prefilledMessage: prefilledMessage || 'undefined',
      goalContext: goalContext || 'undefined',
      workoutData: workoutData || 'undefined', 
      fromWorkout: fromWorkout || 'undefined',
      simpleMessage: simpleMessage || 'undefined'
    });

    // 1) Prefer navigation state for workout analysis
    if (workoutData && fromWorkout) {
      console.log('Found workout data via navigation state, processing...');
      processWorkoutAnalysis(workoutData);
      // Clear navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: undefined });
      return;
    }

    // 2) Handle simple message from navigation (e.g., "Create with Arnold")
    if (simpleMessage && !goalContext && !workoutData) {
      console.log('Found simple message via navigation state, auto-sending...');
      // Clear navigation state first to prevent re-triggering
      navigate(location.pathname, { replace: true, state: undefined });
      try {
        sessionStorage.removeItem(PENDING_CHAT_AUTO_SEND_KEY);
      } catch {
        /* ignore */
      }
      // Mark for auto-send and set input
      pendingAutoSendRef.current = simpleMessage;
      inputValueRef.current = simpleMessage;
      if (textareaRef.current) {
        textareaRef.current.value = simpleMessage;
      }
      setCanSend(false);
      window.setTimeout(() => setCanSend(true), 0);
      return;
    }

    // 2b) Health graph "Analyze" → same auto-send flow as simple message
    const navState = location.state as {
      autoSendHealthAnalysis?: boolean;
      prefilledMessage?: string;
    } | null;
    if (
      prefilledMessage &&
      !goalContext &&
      !workoutData &&
      navState?.autoSendHealthAnalysis
    ) {
      console.log('Health Analyze: auto-sending to Arnold...');
      navigate(location.pathname, { replace: true, state: undefined });
      localStorage.removeItem('pendingHealthAnalysis');
      pendingAutoSendRef.current = prefilledMessage;
      inputValueRef.current = prefilledMessage;
      if (textareaRef.current) {
        textareaRef.current.value = prefilledMessage;
      }
      setCanSend(false);
      window.setTimeout(() => setCanSend(true), 0);
      return;
    }

    // 2) Fallback: check localStorage for pending analysis (in case state was lost)
    try {
      const pendingRaw = localStorage.getItem('pendingWorkoutAnalysis');
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw);
        // Accept only fresh payloads (<= 10 minutes old)
        if (pending && pending.type && (!pending._ts || Date.now() - pending._ts <= 10 * 60 * 1000)) {
          console.log('Using pendingWorkoutAnalysis from localStorage');
          void processWorkoutAnalysis(pending).then(() => {
            localStorage.removeItem('pendingWorkoutAnalysis');
          });
          return;
        } else {
          // Stale payload
          localStorage.removeItem('pendingWorkoutAnalysis');
        }
      }
    } catch (e) {
      console.warn('Failed parsing pendingWorkoutAnalysis from localStorage', e);
      localStorage.removeItem('pendingWorkoutAnalysis');
    }

    try {
      const pendingHealthRaw = localStorage.getItem('pendingHealthAnalysis');
      if (pendingHealthRaw) {
        const pendingHealth = JSON.parse(pendingHealthRaw);
        const pendingMessage = typeof pendingHealth?.message === 'string' ? pendingHealth.message : '';
        if (pendingMessage && (!pendingHealth._ts || Date.now() - pendingHealth._ts <= 10 * 60 * 1000)) {
          console.log('Using pendingHealthAnalysis from localStorage');
          localStorage.removeItem('pendingHealthAnalysis');
          pendingAutoSendRef.current = pendingMessage;
          inputValueRef.current = pendingMessage;
          if (textareaRef.current) {
            textareaRef.current.value = pendingMessage;
          }
          setCanSend(false);
          window.setTimeout(() => setCanSend(true), 0);
          return;
        }
        localStorage.removeItem('pendingHealthAnalysis');
      }
    } catch (e) {
      console.warn('Failed parsing pendingHealthAnalysis from localStorage', e);
      localStorage.removeItem('pendingHealthAnalysis');
    }

    // Handle goal analysis request
    if (prefilledMessage && goalContext) {
      console.log('Processing goal analysis...');
      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: undefined });
      
      // Set loading state immediately
      setIsLoading(true);
      
      // Track the message ID for error handling
      const tempMessageId = Date.now().toString();

      // Send the goal analysis request
      const sendGoalAnalysis = async () => {
        try {
          // Always refresh context to ensure Arnold has latest data
          console.log('Force refreshing context for goal analysis...');
          const refreshedContext = await refetchContext(true);

          // Create conversation if it doesn't exist
          let convId = conversationId;
          if (!convId) {
            const { data: conversation, error: convError } = await supabase
              .from('conversations')
              .insert([{ 
                user_id: user.id, 
                title: 'Goal Analysis - ' + goalContext.exercise_name
              }])
              .select()
              .single();

            if (convError) throw convError;
            convId = conversation.id;
            adoptConversationId(convId);
          }

          // Save user message and add to UI
          const { data: savedUserMessage } = await supabase
            .from('messages')
            .insert([{
              conversation_id: convId,
              content: prefilledMessage,
              sender: 'user'
            }])
            .select()
            .single();

          if (savedUserMessage) {
            const userMessage: Message = {
              id: savedUserMessage.id,
              content: prefilledMessage,
              sender: "user",
              timestamp: savedUserMessage.created_at,
            };
            setMessages(prev => [...prev, userMessage]);
          }

          // Call AI coach with refreshed context and real-time nutrition
          const healthExtras = await getCoachClientExtras(user?.id);
          const { data, error } = await supabase.functions.invoke('ai-coach-chat', {
            body: {
              message: prefilledMessage,
              conversationId: convId,
              userContext: refreshedContext || context,
              clientLocale,
              clientLocalDate: new Date().toLocaleDateString('en-CA'),
              clientTimeZone,
              clientNutritionToday: dailyNutrition,
              clientPlatform: getStraivClientPlatform(),
              ...healthExtras,
              goalContext // Pass goal context to help Arnold focus analysis
            }
          });

          if (error) throw error;

          const aiResponse = data.response;
          const aiMessage: Message = {
            id: (Date.now() + 1).toString(),
            content: formatAiText(aiResponse),
            sender: "ai",
            timestamp: new Date().toISOString(),
          };

          // Save AI message
          await supabase
            .from('messages')
            .insert([{
              conversation_id: convId,
              content: aiResponse,
              sender: 'ai'
            }]);

          setMessages(prev => [...prev, aiMessage]);
          toast.success(t('chat.toasts.analyzed', { coach: profile?.ai_coach_name || 'Arnold' }));
        } catch (error) {
          console.error('Error sending goal analysis:', error);
          toast.error(t('chat.toasts.analyzeFailed'));
          // Remove the user message on error
          setMessages(prev => prev.filter(m => m.id !== tempMessageId));
        } finally {
          setIsLoading(false);
          isSendingRef.current = false; // Reset sending flag
        }
      };

      Analytics.arnoldAnalysisRequested();
      sendGoalAnalysis();
      return;
    }
  }, [messagesLoaded, user, location.state]);

  // Helper: create a unique key for a workout analysis payload
  const getWorkoutKey = (data: any) => `${data?.type || 'unknown'}|${data?.title || ''}|${data?.duration || ''}|${data?._ts || ''}`;

  // Separate function to process workout analysis
  const processWorkoutAnalysis = async (workoutData: any) => {
    const key = getWorkoutKey(workoutData);
    if (processedAnalysisKeysRef.current.has(key)) {
      console.log('Duplicate workout analysis ignored for key:', key);
      return;
    }
    processedAnalysisKeysRef.current.add(key);

    let workoutDetails = '';

    if (workoutData.type === 'running') {
      // Format running workout data - clean, structured, running-specific
      const distance = workoutData.distance ? parseFloat(workoutData.distance) : 0;
      const durationMin = workoutData.duration || 0;
      
      // Build km splits section
      let splitsText = '';
      if (workoutData.kmSplits && Array.isArray(workoutData.kmSplits) && workoutData.kmSplits.length > 0) {
        splitsText = workoutData.kmSplits.map((s: any) => `  km ${s.km}: ${s.pace}/km`).join('\n');
      } else if (Array.isArray(workoutData.splits) && workoutData.splits.length > 0) {
        splitsText = workoutData.splits
          .map((s: any, idx: number) => {
            if (s?.pace) return `  km ${s.km ?? s.kilometer ?? idx + 1}: ${s.pace}/km`;
            if (s?.time) return `  km ${s.km ?? s.kilometer ?? idx + 1}: ${s.time}`;
            return null;
          })
          .filter(Boolean)
          .join('\n');
      } else if (workoutData.splits && typeof workoutData.splits === 'string' && workoutData.splits !== 'No splits recorded') {
        splitsText = workoutData.splits;
      }

      // Calculate pace if not provided
      let paceDisplay = workoutData.pace || '';
      if (!paceDisplay && distance > 0 && durationMin > 0) {
        const totalSec = durationMin * 60;
        const pacePerKm = totalSec / distance;
        const paceMin = Math.floor(pacePerKm / 60);
        const paceSec = Math.round(pacePerKm % 60);
        paceDisplay = `${paceMin}:${paceSec.toString().padStart(2, '0')}/km`;
      }

      const hrText = formatHeartRateSummaryForCoach(workoutData.heartRate);

      if (user?.id && hrText) {
        try {
          localStorage.setItem(
            ARNOLD_LAST_RUN_KEY,
            JSON.stringify({
              userId: user.id,
              _ts: Date.now(),
              title: workoutData.title,
              coachText: hrText.trim(),
            }),
          );
        } catch {
          /* ignore */
        }
      }

      workoutDetails = `Analyze my running session:

RUNNING SESSION
Title: ${workoutData.title}
Distance: ${distance > 0 ? `${distance} km` : 'Not recorded'}
Duration: ${durationMin} minutes
Average Pace: ${paceDisplay || 'Not calculated'}${workoutData.averageSpeedKmh ? `\nAverage Speed: ${workoutData.averageSpeedKmh} km/h` : ''}${workoutData.estimatedCalories ? `\nCalories: ~${workoutData.estimatedCalories} kcal` : ''}${workoutData.feeling ? `\nFeeling: ${workoutData.feeling}` : ''}${workoutData.pauseCount ? `\nPauses taken: ${workoutData.pauseCount}` : ''}${workoutData.notes ? `\nNotes: ${workoutData.notes}` : ''}${splitsText ? `\n\nKILOMETER SPLITS:\n${splitsText}` : ''}${hrText}

Give me a real coach's read on this run. I want a multi-factor analysis, not a generic write-up:

1. Pacing & splits — strategy used (negative/positive/even), split consistency, and how this run's average pace stacks up against my last few sessions if you have them.
2. Heart-rate zones — interpret the zone times and averages above: where did I actually train (Z1–Z5), was it appropriate for this session type, and what should I target next time?
3. Recovery readout — quickly check my last 48h of sleep, HRV vs my baseline, RHR drift, and readiness. Was today the right intensity given that recovery state, or did I push when I should have backed off (or vice versa)?
4. Fueling — was yesterday's carb load aligned with this effort? How does my protein today compare to my target?
5. What's next — prescribe my NEXT specific running session: type (Z2 / tempo / threshold intervals / long / recovery / rest), exact structure with zone + bpm targets on every phase, and the reason picked from points 2–4.
6. One sharp coaching cue I should remember next time I lace up.

Reference the heart-rate zone data in your analysis and remember it for future run prescriptions in this conversation.

If anything (history, HRV, nutrition) isn't logged for me, just say so for that point and move on — don't invent numbers, and don't pad with generic advice. If this is one of my first runs and there's no comparison data, focus on what's in front of you and set the baseline.`;
    } else if (workoutData.type === 'custom') {
      // Format custom workout data
      const notesSection = workoutData.notes ? `\n- **Notes:** ${workoutData.notes}` : '';
      const caloriesSection = workoutData.caloriesBurned ? `\n- **Estimated Calories Burned:** ${workoutData.caloriesBurned}` : '';
      workoutDetails = `I just completed a custom workout and would love your analysis and feedback!

**Custom Workout Summary:**
- **Activity:** ${workoutData.title}
- **Duration:** ${workoutData.duration} minutes
- **Difficulty:** ${workoutData.difficulty || 'medium'}${caloriesSection}${notesSection}

Give me a real coach's read on this session, not a generic write-up:

1. What this activity actually trained — likely muscle groups, energy system worked, estimated stimulus relative to the difficulty I logged.
2. Recovery readout — quickly check my last 48h of sleep, HRV vs my baseline, RHR drift, readiness. Was this the right session for today's recovery state?
3. Fueling — protein today vs my target, and whether yesterday's intake supported this effort.
4. What's next — recommend a specific next move (push, similar session, active recovery, rest) with a one-line rationale tied to points 2 & 3.
5. One sharp cue I should carry into my next workout.

If recovery, HRV, or nutrition data isn't logged, just say so for that point and move on — don't make up numbers. If I'm new and there's no history yet, focus on what's logged today and set my baseline.`;
    } else {
      // Format strength workout data - clean structure without markdown symbols
      const formatSet = (set: any, setIndex: number) => {
        let setStr = `Set ${setIndex + 1}: ${set.reps} reps`;
        if (set.weight) setStr += ` @ ${set.weight}kg`;
        else setStr += ' (bodyweight)';
        if (set.isDropSet || set.drop_set) setStr += ' [DROP SET]';
        
        // Add PB markers
        const pbMarkers = [];
        if (set.isWeightPB) pbMarkers.push('WEIGHT PB');
        if (set.isRepsPB) pbMarkers.push('REPS PB');
        if (set.isTotalWeightPB) pbMarkers.push('VOLUME PB');
        if (pbMarkers.length > 0) setStr += ` [${pbMarkers.join(', ')}]`;
        
        // Add rest time if recorded
        if (set.restSecondsTaken) {
          const mins = Math.floor(set.restSecondsTaken / 60);
          const secs = set.restSecondsTaken % 60;
          setStr += ` (rest: ${mins > 0 ? `${mins}m ` : ''}${secs}s)`;
        }
        
        return setStr;
      };

      workoutDetails = `I just completed a strength workout and would love your analysis and feedback!

WORKOUT SUMMARY
Title: ${workoutData.title}
Duration: ${workoutData.duration} minutes
Total Sets Completed: ${workoutData.completedSets}${workoutData.notes ? `\nWorkout notes / feedback: ${workoutData.notes}` : ''}

EXERCISES PERFORMED (in order):
${workoutData.exercises ? workoutData.exercises.map((ex: any, index: number) => 
`${ex.exerciseOrder || index + 1}. ${ex.name}
   Muscle Groups: ${ex.muscleGroups.join(', ')}
   Completed: ${ex.sets}/${ex.totalSets} sets
${ex.completedSets.map((set: any, setIndex: number) => `   - ${formatSet(set, setIndex)}`).join('\n')}${ex.notes ? `\n   User feedback / exercise notes: ${ex.notes}` : ''}`
).join('\n\n') : ''}

Give me a real coach's read on this session — multi-factor, not generic:

1. Performance vs trend — for the key compound lifts in this workout, compare today's top set + total volume against my logged history only when that history is available from tools/context. E1RM is an estimate: label it clearly as estimated, and do not claim a PR/prior best unless the data explicitly supports it.
2. Fatigue curve — read the set-by-set drop-off (e.g. straight-set, reverse pyramid, linear drop) and tell me whether my structure is matching my output.
3. Recovery readout — last 48h sleep, HRV vs baseline, RHR drift, readiness. Did my CNS show up today, or did I grind through fatigue I should have respected?
4. Fueling — protein today vs my target, plus a pointer on what to eat in the next 1-2 hours given this volume.
5. What's next — recommend my NEXT session: push the same lifts, deload, swap to a different muscle group, or active recovery — with a one-sentence reason tied to points 1-3.
6. One sharp cue (form, pacing, exercise selection) I should carry into next time.

If any data point (history, HRV, nutrition, prior sets) isn't logged for me, just say so for that point and move on — don't invent numbers. Treat "DROP SET" as intentional intensity work when present. If this is one of my first sessions and there's no comparison history, focus on what's in front of you and set my baseline.`;
    }

    console.log('Formatted workout details:', workoutDetails);

    // IMMEDIATELY add the user message to display
    const userMessage: Message = {
      id: Date.now().toString(),
      content: workoutDetails,
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    // Force update messages state synchronously
    setMessages(prev => {
      if (prev.some(m => m.sender === 'user' && m.content === workoutDetails)) return prev;
      const updated = [...prev, userMessage];
      console.log('Added workout message to chat:', updated.length);
      return updated;
    });

    setIsLoading(true);
    
    // Scroll after message is added (requestAnimationFrame is in scrollToBottom)
    scrollToBottom();

    // Send the workout analysis request in the background
    // Create abort controller for timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), 45000);
    });

    try {
      console.log('Refreshing context for workout analysis...');
      const refreshedContext = await refetchContext(true);

      // Create conversation if it doesn't exist
      let convId = conversationId;
      if (!convId) {
        const { data: conversation, error: convError } = await supabase
          .from('conversations')
          .insert([{ 
            user_id: user!.id, 
            title: 'Workout Analysis - ' + workoutData.title
          }])
          .select()
          .single();

        if (convError) throw convError;
        convId = conversation.id;
        adoptConversationId(convId);
      }

      // Save user message
      await supabase
        .from('messages')
        .insert([{
          conversation_id: convId,
          content: workoutDetails,
          sender: 'user'
        }]);
      void touchConversationUpdatedAt(convId);

      // Call AI coach with refreshed context and real-time nutrition
      const healthExtras = await getCoachClientExtras(user!.id);
      const invokePromise = supabase.functions.invoke('ai-coach-chat', {
        body: {
          message: workoutDetails,
          conversationId: convId,
          userContext: refreshedContext,
          clientLocale,
          clientLocalDate: new Date().toLocaleDateString('en-CA'),
          clientTimeZone,
          clientNutritionToday: dailyNutrition,
          clientPlatform: getStraivClientPlatform(),
          ...healthExtras,
        }
      });

      const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;
      if (error) throw error;

      // Format the response
      const formattedResponse = formatAiText(data.response);
      
      // Extract workout plans, programs, and recipes (but defer adding them until after streaming)
      let workoutPlanData: WorkoutPlan | null = null;
      const workoutPlan = parseWorkoutPlanFromMessage(data.response);
      if (workoutPlan) {
        workoutPlanData = workoutPlan;
      }
      
      // Create AI message with unique ID
      const aiMessageId = (Date.now() + 1).toString();
      const aiMessage: Message = {
        id: aiMessageId,
        content: '', // Will be filled progressively
        sender: "ai",
        timestamp: new Date().toISOString(),
      };

      // Add message with initial content to make it visible immediately
      const initialContent = formattedResponse.slice(0, Math.min(50, formattedResponse.length));
      aiMessage.content = initialContent;
      setMessages(prev => [...prev, aiMessage]);
      
      // Mark as streaming
      isStreamingRef.current = true;
      
      // Small delay to ensure message is rendered, then scroll to show it
      await new Promise(resolve => setTimeout(resolve, 30));
      
      // Immediately scroll to show the initial content
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });

      // Smooth streaming using requestAnimationFrame for consistent timing
      let currentIndex = initialContent.length;
      let lastStateUpdateTime = performance.now();
      let animationFrameId: number;
      
      const streamText = (timestamp: number) => {
        if (currentIndex >= formattedResponse.length) {
          // Streaming complete
          isStreamingRef.current = false;
          
          // Final state update with complete text
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: formattedResponse }
              : msg
          ));
          
          // Add workout plan AFTER text is complete
          setTimeout(() => {
            setMessages(prev => prev.map(msg => {
              if (msg.id === aiMessageId) {
                const updated: Message = { ...msg };
                if (workoutPlanData) {
                  updated.workoutPlan = workoutPlanData;
                  setDetectedWorkoutPlans(prev => ({
                    ...prev,
                    [aiMessageId]: workoutPlanData as any,
                  }));
                  persistDetectedRunningPlans(workoutPlanData as WorkoutPlan);
                }
                return updated;
              }
              return msg;
            }));
            
            setTimeout(() => scrollToBottom(false), 100);
          }, 100);
          return;
        }
        
        // Calculate how many characters to reveal based on elapsed time
        const elapsed = timestamp - lastStateUpdateTime;
        const charsToAdd = Math.floor((elapsed / 1000) * CHARS_PER_SECOND);
        
        if (charsToAdd > 0 && elapsed >= STATE_UPDATE_INTERVAL_MS) {
          currentIndex = Math.min(currentIndex + charsToAdd, formattedResponse.length);
          const revealedText = formattedResponse.slice(0, currentIndex);
          
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: revealedText }
              : msg
          ));
          
          scrollToBottom(false);
          lastStateUpdateTime = timestamp;
        }
        
        // Continue animation
        animationFrameId = requestAnimationFrame(streamText);
      };
      
      // Start the streaming animation
      animationFrameId = requestAnimationFrame(streamText);

      // AI message is already saved by the edge function

    } catch (error: any) {
      Analytics.apiError('workout_analysis', error?.message || 'Unknown error');
      console.error('Error sending workout analysis:', error);
      if (error?.message === 'Request timed out') {
        toast.error(t('chat.toasts.requestTimedOut'));
      } else {
        toast.error(t('chat.toasts.analyzeWorkoutFailed'));
      }
    } finally {
      setIsLoading(false);
      isSendingRef.current = false; // Reset sending flag
    }
  };

  // After history loads: show latest messages (scroll to bottom), not the pinned welcome.
  useEffect(() => {
    if (messagesLoaded && messages.length > 0) {
      const hasOnlyWelcome = messages.length === 1 && messages[0]?.id === 'welcome';
      const delayMs = hasOnlyWelcome ? 150 : 280;
      setTimeout(() => {
        requestAnimationFrame(() => {
          if (hasOnlyWelcome) {
            scrollToTop();
          } else {
            scrollToBottom(true);
          }
        });
      }, delayMs);
    }
  }, [messagesLoaded, messages.length]);

  // Auto-scroll to bottom when new messages are added (after initial load)
  useEffect(() => {
    if (messagesLoaded && messages.length > 0) {
      // Check if the last message is from user (they just sent a message)
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.sender === 'user';
      
      // Force scroll if user just sent a message, otherwise only scroll if near bottom
      setTimeout(() => {
        requestAnimationFrame(() => {
          scrollToBottom(isUserMessage);
        });
      }, 100);
    }
  }, [messages.length]);

  // Use ref to track sending state to prevent race conditions
  const isSendingRef = useRef(false);
  // Track if we have a pending auto-send message (from navigation)
  const pendingAutoSendRef = useRef<string | null>(null);
  const sendMessage = async () => {
    const inputValue = inputValueRef.current;
    const trimmed = inputValue.trim();
    if ((!trimmed && pendingAttachments.length === 0) || !user) return;
    
    // Prevent duplicate sends - check both state and ref
    if (isLoading || isSendingRef.current) {
      console.log('Message send blocked - already sending');
      return;
    }

    // Set sending flag immediately to prevent race conditions
    isSendingRef.current = true;
    setIsLoading(true);

    // CRITICAL: Dismiss keyboard FIRST to avoid iOS keyboard queue timeout
    dismissKeyboard();

    let uploadedMeta: ChatAttachment[] = [];
    const pendingSnapshot = [...pendingAttachments];

    try {
      for (const { file } of pendingSnapshot) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        const safeExt = ext && /^[a-z0-9]+$/i.test(ext) ? ext : 'jpg';
        const path = `${user.id}/${crypto.randomUUID()}.${safeExt}`;
        const { error: upErr } = await supabase.storage.from('coach-chat-attachments').upload(path, file, {
          contentType: file.type,
          upsert: false,
        });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from('coach-chat-attachments').getPublicUrl(path);
        uploadedMeta.push({ url: pub.publicUrl, mime_type: file.type, name: file.name });
      }
    } catch (e) {
      console.error('Coach chat image upload failed', e);
      const errAny = e as { statusCode?: string | number; message?: string };
      const status = String(errAny?.statusCode || '');
      const msg = String(errAny?.message || '');
      if (status === '404' || /bucket not found/i.test(msg)) {
        // The storage bucket migration has not been applied to this project.
        toast.error('Image uploads are not configured yet on the server. Please push the latest Supabase migrations.');
      } else {
        toast.error(t('chat.attachments.uploadFailed'));
      }
      setIsLoading(false);
      isSendingRef.current = false;
      return;
    }

    for (const p of pendingSnapshot) {
      if (p.preview) URL.revokeObjectURL(p.preview);
    }
    setPendingAttachments([]);

    const messageContent = trimmed;
    const messageStartTime = Date.now();
    
    // Track message sent
    Analytics.chatMessageSent(messageContent.length + uploadedMeta.length);
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageContent,
      sender: "user",
      timestamp: new Date().toISOString(),
      attachments: uploadedMeta.length ? uploadedMeta : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    // Clear the input - update ref and DOM directly (no state needed)
    inputValueRef.current = "";
    if (textareaRef.current) {
      textareaRef.current.value = "";
    }
    setCanSend(false);
    
    // Scroll will be handled by the useEffect that watches messages.length
    // But we also do it here with a delay to ensure DOM has updated
    setTimeout(() => {
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    }, 100);

    // Create abort controller for timeout handling
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), 45000);
    });

    try {
      const refreshedContext = await refetchContext(true);

      // Create conversation if it doesn't exist
      let convId = conversationId;
      if (!convId) {
        const titleSeed = messageContent.slice(0, 50) || t('chat.attachments.photoTitle');
        const { data: conversation, error: convError } = await supabase
          .from('conversations')
          .insert([{ 
            user_id: user.id, 
            title: titleSeed.slice(0, 50)
          }])
          .select()
          .single();

        if (convError) throw convError;
        convId = conversation.id;
        adoptConversationId(convId);
      }

      // Save user message (must succeed before calling the coach)
      const { data: savedUserRow, error: userSaveErr } = await supabase
        .from('messages')
        .insert([{
          conversation_id: convId,
          content: messageContent,
          sender: 'user',
          attachments: uploadedMeta.length ? (uploadedMeta as unknown as Json) : null,
        }])
        .select('id, created_at')
        .single();

      if (userSaveErr || !savedUserRow) {
        console.error('[Chat] failed to save user message:', userSaveErr);
        throw userSaveErr ?? new Error('Failed to save your message');
      }

      void touchConversationUpdatedAt(convId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === userMessage.id
            ? { ...m, id: savedUserRow.id, timestamp: savedUserRow.created_at }
            : m,
        ),
      );

      const healthExtras = await getCoachClientExtras(user.id);
      const invokeBody: Record<string, unknown> = {
        message: messageContent,
        conversationId: convId,
        userContext: refreshedContext,
        clientLocale,
        clientLocalDate: new Date().toLocaleDateString('en-CA'),
        clientTimeZone,
        clientNutritionToday: dailyNutrition,
        clientNutritionGoals: nutritionGoals
          ? {
              daily_calories: nutritionGoals.daily_calories,
              daily_protein: nutritionGoals.daily_protein,
              daily_carbs: nutritionGoals.daily_carbs,
              daily_fat: nutritionGoals.daily_fat,
              daily_fiber: nutritionGoals.daily_fiber,
              is_manual_override: nutritionGoals.is_manual_override,
            }
          : null,
        clientPlatform: getStraivClientPlatform(),
        ...healthExtras,
      };
      if (uploadedMeta.length > 0) {
        invokeBody.imageUrls = uploadedMeta.map((a) => a.url);
      }

      // Call AI coach with cached context and real-time nutrition
      const invokePromise = supabase.functions.invoke('ai-coach-chat', {
        body: invokeBody,
      });

      const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;
      
      // Track response time
      const responseTime = Date.now() - messageStartTime;
      Analytics.chatResponseReceived(responseTime);

      // Debug log the full response
      console.log('AI response:', { data, error, hasError: !!error });

      // Handle edge function errors explicitly (e.g. daily_limit_reached)
      if (error) {
        console.log('Error object:', JSON.stringify(error, null, 2));
        
        const errAny: any = error;
        const ctx: any = errAny.context || {};
        
        // Supabase returns the response body in context for non-2xx responses
        // Try to parse if it's a string
        let parsedContext = ctx;
        if (typeof ctx === 'string') {
          try {
            parsedContext = JSON.parse(ctx);
          } catch {
            parsedContext = {};
          }
        }
        
        // Extract error code and message from various possible locations
        const code = parsedContext?.error || ctx?.error || errAny.error || errAny.code;
        const msg: string = parsedContext?.message || ctx?.message || errAny.message || '';
        
        console.log('Extracted error:', { code, msg });

        // Check for daily limit
        const limitRegex = /daily_limit_reached|reached today.s limit of AI requests|today's limit of AI requests/i;
        if (code === 'coach_weekly_limit') {
          Analytics.aiDailyLimitReached();
          toast.error(msg || t('chat.toasts.coachWeeklyLimit'));
          isSendingRef.current = false;
          return;
        }
        if (code === 'coach_chat_image_daily_limit') {
          toast.error(msg || t('chat.attachments.toastDailyLimit'));
          setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
          isSendingRef.current = false;
          setIsLoading(false);
          return;
        }
        if (code === 'daily_limit_reached' || limitRegex.test(msg) || limitRegex.test(JSON.stringify(error))) {
          Analytics.aiDailyLimitReached();
          toast.error(msg || t('chat.toasts.dailyAiLimit'));
          isSendingRef.current = false;
          return;
        }

        if (code === 'too_many_requests' || /429/.test(JSON.stringify(error))) {
          toast.error(t('chat.toasts.rateLimit'));
          isSendingRef.current = false;
          return;
        }

        if (/402/.test(JSON.stringify(error))) {
          toast.error(t('chat.toasts.creditsExhausted'));
          isSendingRef.current = false; // Reset before return
          return;
        }

        // For any other edge function error, fall through to generic handler
        throw error;
      }
      
      // Also check if the data itself contains an error (some edge functions return errors in data)
      // Edge function now returns 200 with error in body for rate limits
      if (data?.error || data?.rateLimitExceeded) {
        console.log('Error in data:', data);
        const code = data.error;
        const msg = data.message || '';
        
        if (code === 'coach_weekly_limit' || /coach_weekly|weekly coach messages/i.test(msg)) {
          toast.error(msg || t('chat.toasts.coachWeeklyLimit'));
          isSendingRef.current = false;
          return;
        }
        if (code === 'coach_chat_image_daily_limit') {
          toast.error(msg || t('chat.attachments.toastDailyLimit'));
          setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
          isSendingRef.current = false;
          setIsLoading(false);
          return;
        }
        if (code === 'daily_limit_reached' || data?.rateLimitExceeded || /daily_limit|today.s limit/i.test(msg)) {
          toast.error(msg || t('chat.toasts.dailyAiLimit'));
          isSendingRef.current = false;
          return;
        }
        
        if (code === 'too_many_requests') {
          toast.error(t('chat.toasts.rateLimitShort'));
          isSendingRef.current = false; // Reset before return
          return;
        }
        
        throw new Error(msg || 'Unknown error from AI');
      }

      if (!data) {
        throw new Error('No response received from your coach.');
      }

      void refreshCoachImageUsage();

      if (data.conversationId) {
        convId = data.conversationId;
        adoptConversationId(convId);
      }
      void touchConversationUpdatedAt(convId);

      // Format the response with proper spacing
      const formattedResponse = formatAiText(data.response);
      
      // Create AI message - start with empty content for streaming effect
      const aiMessageId = data.messageId || (Date.now() + 1).toString();
      const aiMessage: Message = {
        id: aiMessageId,
        content: '', // Start empty for progressive reveal
        sender: "ai",
        timestamp: new Date().toISOString(),
      };

      console.log('AI response data:', { hasWorkoutPlan: !!data.workoutPlan, messageId: data.messageId });

      // Store workout plan/program/recipes data separately - will be added AFTER text streaming completes
      let workoutPlanData: WorkoutPlan | null = null;
      let workoutProgramData: WorkoutProgramData | null = null;
      let recipesData: any[] | null = null;

      // Prefer backend-extracted workout plan; fallback to local parsing
      if (data.workoutPlan) {
        console.log('Using backend workout plan:', data.workoutPlan);
        workoutPlanData = data.workoutPlan as any;
      } else {
        const workoutPlan = parseWorkoutPlanFromMessage(data.response);
        if (workoutPlan) {
          console.log('Parsed workout plan from response:', workoutPlan);
          workoutPlanData = workoutPlan;
        }
      }

      // Check if response contains workout program data
      if (data.workoutProgramData) {
        workoutProgramData = data.workoutProgramData;
        Analytics.arnoldProgramGenerated();
      }

      // Check if response contains recipes data
      if (data.recipesData && Array.isArray(data.recipesData) && data.recipesData.length > 0) {
        console.log('Recipes detected:', data.recipesData.length);
        recipesData = data.recipesData;
        Analytics.arnoldRecipeSuggested();
      }

      // Add message with initial content to make it visible immediately
      const initialContent = formattedResponse.slice(0, Math.min(50, formattedResponse.length));
      aiMessage.content = initialContent;
      setMessages(prev => [...prev, aiMessage]);
      
      // Mark as streaming
      isStreamingRef.current = true;
      
      // Small delay to ensure message is rendered, then scroll to show it
      await new Promise(resolve => setTimeout(resolve, 30));
      
      // Immediately scroll to show the initial content
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });

      // Smooth streaming using requestAnimationFrame for consistent timing
      // This eliminates the choppy setInterval behavior on Android
      let currentIndex = initialContent.length;
      let lastStateUpdateTime = performance.now();
      let animationFrameId: number;
      
      const streamText = (timestamp: number) => {
        if (currentIndex >= formattedResponse.length) {
          // Streaming complete
          isStreamingRef.current = false;
          
          // Final state update with complete text
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: formattedResponse }
              : msg
          ));
          
          // Add workout plan/program/recipes AFTER text is complete
          setTimeout(() => {
            setMessages(prev => prev.map(msg => {
              if (msg.id === aiMessageId) {
                const updated: Message = { ...msg };
                if (workoutPlanData) {
                  updated.workoutPlan = workoutPlanData;
                  setDetectedWorkoutPlans(prev => ({
                    ...prev,
                    [aiMessageId]: workoutPlanData as any,
                  }));
                  persistDetectedRunningPlans(workoutPlanData as WorkoutPlan);
                }
                if (workoutProgramData) {
                  updated.workoutProgramData = workoutProgramData;
                }
                if (recipesData) {
                  updated.recipesData = recipesData;
                  setSelectedRecipes(recipesData);
                  setCurrentRecipeIndex(0);
                  setShowRecipeModal(true);
                }
                return updated;
              }
              return msg;
            }));
            
            setTimeout(() => scrollToBottom(false), 100);
          }, 100);
          return;
        }
        
        // Calculate how many characters to reveal based on elapsed time
        const elapsed = timestamp - lastStateUpdateTime;
        const charsToAdd = Math.floor((elapsed / 1000) * CHARS_PER_SECOND);
        
        if (charsToAdd > 0 && elapsed >= STATE_UPDATE_INTERVAL_MS) {
          currentIndex = Math.min(currentIndex + charsToAdd, formattedResponse.length);
          const revealedText = formattedResponse.slice(0, currentIndex);
          
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: revealedText }
              : msg
          ));
          
          scrollToBottom(false);
          lastStateUpdateTime = timestamp;
        }
        
        // Continue animation
        animationFrameId = requestAnimationFrame(streamText);
      };
      
      // Start the streaming animation
      animationFrameId = requestAnimationFrame(streamText);

      // Ensure persistence of AI message if backend didn't save it
      if (!data.messageId && convId) {
        try {
          await supabase
            .from('messages')
            .insert([{ conversation_id: convId, content: data.response, sender: 'ai' }]);
        } catch (e) {
          console.warn('Failed to persist AI message (fallback):', e);
        }
      }
    } catch (error: any) {
      // Check if this is a retryable error (network/timeout)
      const rawMsg = String(error?.message || error || '');
      let serialized = rawMsg;
      try {
        serialized += ' ' + JSON.stringify(error);
      } catch {
        // ignore JSON stringify issues
      }

      const isRetryable = /timeout|timed out|fetch|network|ECONNRESET|ETIMEDOUT|failed to fetch/i.test(serialized);
      const errAny: any = error || {};
      const structuredCode = errAny.error || errAny.code || errAny?.context?.error;
      const structuredMessage = errAny.message || errAny?.context?.message || rawMsg;
      const limitRegex = /daily_limit_reached|reached today.s limit of AI requests|today's limit of AI requests/i;
      const isDailyLimit = limitRegex.test(serialized) || structuredCode === 'daily_limit_reached';
      const isTooManyRequests = /429/.test(serialized) || structuredCode === 'too_many_requests';
      const isPaymentRequired = /402/.test(serialized);

      // Non-retryable errors - show message and don't mark as failed
      if (isDailyLimit) {
        Analytics.aiDailyLimitReached();
        toast.error(structuredMessage || t('chat.toasts.dailyAiLimit'));
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      } else if (isTooManyRequests) {
        toast.error(t('chat.toasts.rateLimitShort'));
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      } else if (isPaymentRequired) {
        toast.error(t('chat.toasts.creditsExhaustedShort'));
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      } else if (isRetryable) {
        // Perform automatic retry (up to 3 attempts)
        console.log('Network error detected, attempting automatic retry...');
        Analytics.apiError('chat_message', error?.message || 'Network error');
        
        let retrySuccess = false;
        const RETRY_DELAYS = [1000, 2000, 4000];
        
        for (let attempt = 0; attempt < 3; attempt++) {
          console.log(`Auto-retry attempt ${attempt + 1}/3...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          
          try {
            const retryTimeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Request timed out')), 45000);
            });
            
            const retryHealthExtras = await getCoachClientExtras(user?.id);
            const retryInvokePromise = supabase.functions.invoke('ai-coach-chat', {
              body: {
                message: messageContent,
                conversationId: conversationId,
                userContext: context,
                clientLocale,
                clientLocalDate: new Date().toLocaleDateString('en-CA'),
          clientTimeZone,
                clientNutritionToday: dailyNutrition,
                clientPlatform: getStraivClientPlatform(),
                ...retryHealthExtras,
                isRetry: true,
                ...(uploadedMeta.length > 0
                  ? { imageUrls: uploadedMeta.map((a) => a.url) }
                  : {}),
              }
            });

            const retryResult = await Promise.race([retryInvokePromise, retryTimeoutPromise]) as any;
            
            if (retryResult.error) throw retryResult.error;
            if (!retryResult.data) throw new Error('No response');
            if (retryResult.data?.error) throw new Error(retryResult.data.message);

            // Success! Process the response
            const formattedResponse = formatAiText(retryResult.data.response);
            const aiMessageId = retryResult.data.messageId || (Date.now() + 1).toString();
            const aiMessage: Message = {
              id: aiMessageId,
              content: formattedResponse,
              sender: "ai",
              timestamp: new Date().toISOString(),
            };

            if (retryResult.data.workoutPlan) {
              aiMessage.workoutPlan = retryResult.data.workoutPlan;
              setDetectedWorkoutPlans(prev => ({ ...prev, [aiMessageId]: retryResult.data.workoutPlan }));
              persistDetectedRunningPlans(retryResult.data.workoutPlan as WorkoutPlan);
            }
            if (retryResult.data.workoutProgramData) {
              aiMessage.workoutProgramData = retryResult.data.workoutProgramData;
            }
            if (retryResult.data.recipesData?.length > 0) {
              aiMessage.recipesData = retryResult.data.recipesData;
            }

            setMessages(prev => [...prev, aiMessage]);
            
            // Persist AI message if needed
            if (!retryResult.data.messageId && conversationId) {
              try {
                await supabase.from('messages')
                  .insert([{ conversation_id: conversationId, content: retryResult.data.response, sender: 'ai' }]);
              } catch (e) {
                console.warn('Failed to persist AI message:', e);
              }
            }
            
            retrySuccess = true;
            console.log('Auto-retry succeeded on attempt', attempt + 1);
            break;
          } catch (retryErr: any) {
            console.error(`Auto-retry attempt ${attempt + 1} failed:`, retryErr);
            // Check if non-retryable
            const retryErrStr = JSON.stringify(retryErr) + (retryErr?.message || '');
            if (/daily_limit|402|too_many_requests/.test(retryErrStr)) {
              break; // Stop retrying
            }
          }
        }

        if (!retrySuccess) {
          // Mark message as failed after all retries exhausted
          setMessages(prev => prev.map(m => 
            m.id === userMessage.id ? { ...m, failed: true } : m
          ));
          toast.error(t('chat.toasts.couldntReachCoach', { coach: profile?.ai_coach_name || 'your coach' }));
        }
      } else {
        // Other errors - mark as failed
        Analytics.apiError('chat_message', error?.message || 'Unknown error');
        console.error('Error sending message:', error);
        setMessages(prev => prev.map(m => 
          m.id === userMessage.id ? { ...m, failed: true } : m
        ));
        toast.error(t('chat.toasts.coachCouldntRespond'));
      }
    } finally {
      setIsLoading(false);
      isSendingRef.current = false;
    }
  };

  // Retry handler for failed messages
  const handleRetryMessage = useCallback(async (messageId: string) => {
    const failedMessage = messages.find(m => m.id === messageId && m.failed);
    if (!failedMessage || !user) return;

    // Prevent duplicate retries
    if (isLoading || isSendingRef.current) return;

    isSendingRef.current = true;
    setIsLoading(true);

    // Mark message as retrying
    setMessages(prev => prev.map(m => 
      m.id === messageId ? { ...m, retrying: true, failed: false } : m
    ));

    const messageContent = failedMessage.content;
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff

    let lastError: any = null;
    let success = false;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt + 1}/${MAX_RETRIES} for message...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt - 1]));
      }

      try {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timed out')), 45000);
        });

        let convId = conversationId;
        if (!convId) {
          const { data: conversation, error: convError } = await supabase
            .from('conversations')
            .insert([{ user_id: user.id, title: messageContent.slice(0, 50) }])
            .select()
            .single();
          if (convError) throw convError;
          convId = conversation.id;
          adoptConversationId(convId);
        }

        const retryHealthExtras = await getCoachClientExtras(user.id);
        const invokePromise = supabase.functions.invoke('ai-coach-chat', {
          body: {
            message: messageContent,
            conversationId: convId,
            userContext: context,
            clientLocale,
            clientLocalDate: new Date().toLocaleDateString('en-CA'),
          clientTimeZone,
            clientNutritionToday: dailyNutrition,
            clientPlatform: getStraivClientPlatform(),
            ...retryHealthExtras,
            isRetry: true,
            ...(failedMessage.attachments?.length
              ? { imageUrls: failedMessage.attachments.map((a) => a.url) }
              : {}),
          }
        });

        const { data, error } = await Promise.race([invokePromise, timeoutPromise]) as any;

        if (error) {
          // Check for non-retryable errors
          const serialized = JSON.stringify(error);
          if (/daily_limit|402|too_many_requests|coach_chat_image_daily_limit/.test(serialized)) {
            throw error; // Don't retry these
          }
          throw error;
        }

        if (data?.error || data?.rateLimitExceeded) {
          throw new Error(data.message || 'Unknown error from AI');
        }

        if (!data) {
          throw new Error('No response received from your coach.');
        }

        // Success! Process the response
        const formattedResponse = formatAiText(data.response);
        const aiMessageId = data.messageId || (Date.now() + 1).toString();

        // Clear the retrying state
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, retrying: false, failed: false } : m
        ));

        // Add AI response
        const aiMessage: Message = {
          id: aiMessageId,
          content: formattedResponse,
          sender: "ai",
          timestamp: new Date().toISOString(),
        };

        // Check for workout/recipe data
        if (data.workoutPlan) {
          aiMessage.workoutPlan = data.workoutPlan;
          setDetectedWorkoutPlans(prev => ({ ...prev, [aiMessageId]: data.workoutPlan }));
          persistDetectedRunningPlans(data.workoutPlan as WorkoutPlan);
        }
        if (data.workoutProgramData) {
          aiMessage.workoutProgramData = data.workoutProgramData;
        }
        if (data.recipesData?.length > 0) {
          aiMessage.recipesData = data.recipesData;
        }

        setMessages(prev => [...prev, aiMessage]);
        toast.success(t('chat.toasts.messageSent'));
        success = true;
        break;

      } catch (err: any) {
        lastError = err;
        console.error(`Attempt ${attempt + 1} failed:`, err);
        
        // Check if error is non-retryable
        const errStr = JSON.stringify(err) + (err?.message || '');
        if (/daily_limit|402|too_many_requests|coach_chat_image_daily_limit|daily limit/.test(errStr)) {
          break; // Don't retry rate limit or payment errors
        }
      }
    }

    if (!success) {
      // All retries exhausted
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, retrying: false, failed: true } : m
      ));
      
      const errStr = JSON.stringify(lastError) + (lastError?.message || '');
      if (/daily_limit/.test(errStr)) {
        toast.error(t('chat.toasts.dailyLimit'));
      } else {
        toast.error(t('chat.toasts.messageFailed'));
      }
    }

    setIsLoading(false);
    isSendingRef.current = false;
  }, [messages, user, conversationId, context, dailyNutrition, formatAiText]);

  // Auto-send message when set from navigation state
  useEffect(() => {
    if (pendingAutoSendRef.current && inputValueRef.current === pendingAutoSendRef.current && !isLoading && !isSendingRef.current) {
      console.log('Auto-sending pending message:', pendingAutoSendRef.current);
      pendingAutoSendRef.current = null; // Clear to prevent re-trigger
      // Small delay to ensure UI has updated
      setTimeout(() => {
        sendMessage();
      }, 150);
    }
  }, [canSend, isLoading]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleWorkoutImport = (formattedWorkout: string, workoutData?: any) => {
    if (workoutData) {
      void processWorkoutAnalysis({
        ...workoutData,
        _ts: Date.now(),
      });
      return;
    }

    // Fallback: add the workout data and position cursor after it for user to add their own text
    const value = formattedWorkout + "\n\n💬 ";
    inputValueRef.current = value;
    if (textareaRef.current) {
      textareaRef.current.value = value;
      textareaRef.current.focus();
    }
    setCanSend(true);
  };

  const handleImportWorkoutProgram = useCallback((messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message?.workoutProgramData) return;

    setSelectedProgramData(message.workoutProgramData);
    setShowProgramModal(true);
  }, [messages]);

  const importWorkoutProgram = async () => {
    if (!selectedProgramData) return;

    setIsImportingProgram(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-workout-program', {
        body: { workoutProgramData: selectedProgramData }
      });

      if (error) throw error;

      Analytics.arnoldProgramSaved();
      toast.success(t('chat.toasts.programImported'), {
        duration: 5000,
      });

      setShowProgramModal(false);
      setSelectedProgramData(null);
    } catch (error) {
      console.error('Error importing workout program:', error);
      toast.error(t('chat.toasts.programImportFailed'));
    } finally {
      setIsImportingProgram(false);
    }
  };

  const handleStartLater = useCallback(async (messageId: string) => {
    const workoutPlan = detectedPlansRef.current[messageId];
    console.log('Start later clicked for message:', messageId, 'Plan:', workoutPlan);
    
    if (!workoutPlan || !user) {
      console.error('No workout plan or user:', { workoutPlan, user: !!user });
      return;
    }

    try {
      // 1) Ensure conversation exists
      let convId = conversationId;
      if (!convId) {
        const { data: conversation, error: convErr } = await supabase
          .from('conversations')
          .insert([{ user_id: user.id, title: "AI Coaching" }])
          .select()
          .single();
        if (convErr) throw convErr;
        convId = conversation.id;
        adoptConversationId(convId);
      }

      // 2) Persist the plan as an AI message so it always appears in Chat and Import modal
      const planJson = JSON.stringify({ type: 'single_workout', exercises: (workoutPlan as any).exercises || [] });
      const content = `Saved for later.\n\n` + '```json' + `\n${planJson}\n` + '```';

      const { data: savedMsg, error: msgErr } = await supabase
        .from('messages')
        .insert([{ conversation_id: convId, content, sender: 'ai' }])
        .select()
        .single();
      if (msgErr) throw msgErr;

      // Add to local chat state so user sees it immediately
      const newMessage: Message = {
        id: savedMsg.id,
        content: formatAiText(content),
        sender: 'ai',
        timestamp: savedMsg.created_at,
        workoutPlan: { type: 'single_workout', exercises: (workoutPlan as any).exercises || [] } as any,
      };
      setMessages(prev => [...prev, newMessage]);
      setDetectedWorkoutPlans(prev => ({ ...prev, [savedMsg.id]: newMessage.workoutPlan! }));

      // 3) Also save as workout template for quicker lookup (best-effort)
      const payload: any = {
        user_id: user.id,
         title: "AI Workout Plan",
        exercises: (workoutPlan as any).exercises || [],
        source: 'ai',
        original_message_id: savedMsg.id,
      };
      const { error: tmplErr } = await supabase.from('workout_templates').insert(payload);
      if (tmplErr) console.warn('Template save failed (non-blocking):', tmplErr);

      toast.success(t('chat.toasts.savedToChat'));
    } catch (e) {
      console.error('Failed to save workout template/message:', e);
      toast.error(t('chat.toasts.saveLaterFailed'));
    }
  }, [user, conversationId]);

  const handleImportWorkoutPlan = useCallback((messageId: string) => {
    const workoutPlan = detectedPlansRef.current[messageId];
    if (!workoutPlan) return;

    if (workoutPlan.type === 'single_workout') {
      const exercises = convertToWorkoutLoggerFormat(workoutPlan);
      if (exercises) {
        localStorage.removeItem('activeWorkoutSession');
        localStorage.setItem('importedWorkout', JSON.stringify({
          title: "AI Workout Plan",
          exercises,
          imported: true,
        }));
        Analytics.arnoldWorkoutAccepted();
        toast.success(t('chat.toasts.workoutPlanImported'));
        navigate('/gym-workout');
      }
    } else if (workoutPlan.type === 'weekly_plan') {
      let weeklyPlanText = `**Weekly Workout Plan from ${profile?.ai_coach_name || 'your Coach'}:**\n\n`;
      workoutPlan.days.forEach((day) => {
        weeklyPlanText += `**${day.day} - ${day.workout_name}:**\n`;
        day.exercises.forEach((exercise, exIndex) => {
          const weightText = exercise.weight_kg ? ` @ ${exercise.weight_kg}kg` : '';
          weeklyPlanText += `${exIndex + 1}. ${exercise.exercise_name} - ${exercise.sets} sets × ${exercise.reps} reps${weightText}\n`;
        });
        weeklyPlanText += "\n";
      });
      const value = weeklyPlanText + "\n\n💬 ";
      inputValueRef.current = value;
      if (textareaRef.current) {
        textareaRef.current.value = value;
        textareaRef.current.focus();
      }
      setCanSend(true);
      toast.success(t('chat.toasts.weeklyPlanImported'));
    }
  }, [navigate]);

  const handleImportRunningWorkout = useCallback((messageId: string, workoutIndex?: number) => {
    const workoutPlan = detectedPlansRef.current[messageId];
    if (!workoutPlan) return;

    let selected: RunningWorkoutPlan | null = null;
    let allPlans: RunningWorkoutPlan[] = [];
    if (isRunningWorkoutPlan(workoutPlan)) {
      selected = workoutPlan;
      allPlans = [workoutPlan];
    } else if (isRunningWorkoutCollection(workoutPlan)) {
      allPlans = workoutPlan.workouts;
      selected = workoutPlan.workouts[workoutIndex ?? 0] ?? null;
    }
    if (!selected) return;

    let planText = '';
    try {
      planText = formatRunningWorkoutForCoach(selected);
    } catch (formatErr) {
      console.error('Failed to format running workout for coach:', formatErr);
      toast.error('Could not load this run workout. Try asking Arnold again.');
      return;
    }

    try {
      const suggestedAt =
        messages.find((m) => m.id === messageId)?.timestamp || new Date().toISOString();
      appendArnoldRunPlans(allPlans.map((plan) => ({ plan, suggestedAt })));
    } catch (syncErr) {
      console.warn('Failed to persist Arnold run plans:', syncErr);
    }

    const currentSettings = (() => {
      try {
        return JSON.parse(localStorage.getItem('runningUserSettings') || '{}');
      } catch {
        return {};
      }
    })();

    localStorage.setItem('runningUserSettings', JSON.stringify({
      ...currentSettings,
      activityType: currentSettings.activityType || 'outdoor',
      audioStats: currentSettings.audioStats || '5min',
      audioVoice: currentSettings.audioVoice || 'female',
      coachGuide: {
        enabled: true,
        frequency: currentSettings.coachGuide?.frequency || 'when_needed',
        plan: planText,
        source: 'arnold',
      },
    }));
    toast.success('Running workout imported to AI run guide');
    navigate('/running-start', { state: { openCoachGuide: true, coachGuidePlan: planText } });
  }, [navigate, messages]);

  // Quick action handler - sends simple, natural prompts to Arnold
  // Arnold already knows the user's context, goals, and preferences
  const handleQuickAction = useCallback((action: string) => {
    if (isLoading) return;

    let prompt = '';

    switch (action) {
      case 'single_workout':
        prompt = 'Create a workout for me.';
        break;

      case 'recipe':
        prompt = 'Give me recipes that fit my goals.';
        break;

      case 'weekly_program': {
        if (!isPremiumActive(profile)) {
          toast.error(t('premium.weeklyProgramLocked'));
          return;
        }
        prompt = 'Create a weekly workout program for me.';
        break;
      }

      case 'rename_coach': {
        const newName = window.prompt('Enter a new name for your AI coach:', profile?.ai_coach_name || 'Arnold');
        if (newName && newName.trim() && newName.trim() !== (profile?.ai_coach_name || 'Arnold')) {
          updateProfile({ ai_coach_name: newName.trim() } as any).then(({ error }) => {
            if (!error) {
              toast.success(t('chat.toasts.coachRenamed', { name: newName.trim() }));
              setMessages(prev => prev.map(m => 
                m.id === 'welcome' ? { ...m, content: generateWelcomeMessage() } : m
              ));
            } else {
              toast.error(t('chat.toasts.renameFailed'));
            }
          });
        }
        return;
      }

      default:
        return;
    }

    // Set the input and trigger send
    inputValueRef.current = prompt;
    if (textareaRef.current) {
      textareaRef.current.value = prompt;
    }
    setCanSend(true);
    // Use a ref-based trigger to auto-send after state update
    pendingAutoSendRef.current = prompt;
    
    // Don't scroll here - let the message sending handle the scroll after message is added
  }, [isLoading, profile, t, updateProfile]);

  const isNative = Capacitor.isNativePlatform();
  
  return (
    <div 
      className="w-full flex flex-col"
      style={{
        height: '100%',
        minHeight: 0,
        position: 'relative',
      }}
    >
      {/* Chat Header - Custom header for Arnold */}
      <div 
        className="flex-shrink-0 border-b border-border/50 bg-gradient-card"
        style={{ 
          height: `calc(${SAFE_AREA.top} + 56px)`,
          paddingTop: SAFE_AREA.top,
          position: 'relative',
          zIndex: 10,
        }}
      >
        <div className="flex items-center gap-3 px-4" style={{ height: '56px' }}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="shrink-0 hover:bg-muted/20 h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <img src={arnoldLogo} alt="Arnold" className="h-10 w-10 rounded-xl" />
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold">{profile?.ai_coach_name || 'Arnold'}</h1>
            <p className="text-xs text-muted-foreground">
              {t('chat.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area - Scrollable */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4"
        ref={scrollAreaRef}
        style={{
          minHeight: 0,
          overscrollBehavior: 'contain',
          maxWidth: '100%',
          width: '100%',
          // Space for input area + bottom nav or keyboard
          paddingBottom: isWebPlatform 
            ? '240px' 
            : keyboardHeight > 0
              ? (isAndroidPlatform ? '200px' : `calc(180px + ${keyboardHeight}px)`) // Android: viewport shrunk, just input space
              : `calc(180px + 56px + ${SAFE_AREA.bottom})`, // Keyboard closed (input directly above 56px nav)
          position: 'relative',
          zIndex: 1,
          transition: 'padding-bottom 0.25s ease-out',
        }}
      >
        <div className="flex flex-col gap-4 w-full max-w-full" style={{ width: '100%', maxWidth: '100%' }}>
          {loadingOlderHistory ? (
            <p className="text-center text-xs text-muted-foreground py-1">Loading earlier messages…</p>
          ) : null}
          {useMemo(() => messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              detectedWorkoutPlans={detectedWorkoutPlans}
              handleImportWorkoutPlan={handleImportWorkoutPlan}
              handleImportWorkoutProgram={handleImportWorkoutProgram}
              handleImportRunningWorkout={handleImportRunningWorkout}
              handleStartLater={handleStartLater}
              handleShowRecipes={(recipes) => {
                setSelectedRecipes(recipes);
                setCurrentRecipeIndex(0);
                setShowRecipeModal(true);
              }}
              navigate={navigate}
              onQuickAction={handleQuickAction}
              isWelcomeMessage={message.id === 'welcome'}
              onRetry={handleRetryMessage}
              coachName={profile?.ai_coach_name || 'Arnold'}
              userAvatarUrl={profile?.avatar_url}
            />
          )), [messages, detectedWorkoutPlans, handleImportWorkoutPlan, handleImportWorkoutProgram, handleImportRunningWorkout, handleStartLater, navigate, handleQuickAction, handleRetryMessage, profile?.ai_coach_name, profile?.avatar_url])}
          
          {/* Thinking indicator */}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <img src={arnoldAvatarGif} alt="Arnold" className="flex-shrink-0 rounded-lg" style={{ height: '61px', width: '61px' }} />
              <div className="bg-gradient-card border border-border/50 p-4 rounded-2xl">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm">{t('chat.thinking', { coach: profile?.ai_coach_name || 'Arnold' })}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Area - Fixed at bottom. Same logic as iOS for both platforms. */}
      <div 
        ref={inputAreaRef}
        className="flex-shrink-0 p-3 border-t border-border/50 bg-gradient-card"
        style={{
          position: (isNative || isWebPlatform) ? 'fixed' : 'relative',
          bottom: isNative
            ? keyboardHeight > 0
              ? (isAndroidPlatform ? 0 : keyboardHeight) // Android: bottom bar hidden + adjustResize = bottom:0 at keyboard top
              : `calc(56px + ${SAFE_AREA.bottom})` // Keyboard closed: directly above bottom nav, no gap
            : isWebPlatform
              ? '56px'
              : 'auto',
          left: (isNative || isWebPlatform) ? 0 : 'auto',
          right: (isNative || isWebPlatform) ? 0 : 'auto',
          paddingLeft: SAFE_AREA.left,
          paddingRight: SAFE_AREA.right,
          zIndex: (isNative || isWebPlatform) ? 10001 : 'auto', // Above bottom nav (9999) so input is never covered
          width: (isNative || isWebPlatform) ? '100%' : 'auto',
          backgroundColor: 'hsl(0 0% 18%)',
          boxSizing: 'border-box',
          transition: 'bottom 0.25s ease-out', // Smooth transition when keyboard shows/hides
        }}
      >
          <div className="flex flex-wrap gap-1.5 mb-2 min-w-0">
            {/* Return to Workout Button (if coming from workout) */}
            {location.state?.fromWorkout && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="h-8 px-2.5 text-xs shrink-0 border-primary/20 text-primary hover:bg-primary/10"
                title={t('chat.returnToWorkoutTitle')}
              >
                {t('chat.backToWorkout')}
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWorkoutModal(true)}
              className="h-8 px-2.5 text-xs shrink-0 border-border/50 hover:bg-secondary/10"
            >
              {t('chat.analyzeWorkout')}
            </Button>

            <input
              ref={chatImageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={onCoachChatFilesSelected}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openCoachChatImagePicker}
              disabled={
                isLoading ||
                coachImagesUsedToday + pendingAttachments.length >= COACH_CHAT_IMAGES_PER_DAY
              }
              className="h-8 px-2.5 text-xs shrink-0 border-border/50 hover:bg-secondary/10"
              title={t('chat.attachments.addPhotos')}
            >
              {t('chat.attachments.toolbarButton')}
            </Button>
          </div>
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {pendingAttachments.map((p, idx) => (
                <div key={`${p.preview}-${idx}`} className="relative">
                  <img src={p.preview} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                  <button
                    type="button"
                    onClick={() => removePendingAttachment(idx)}
                    className="absolute -top-1 -right-1 rounded-full bg-background border p-0.5 shadow"
                    aria-label={t('chat.attachments.remove')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 min-w-0">
            <Textarea
              ref={textareaRef}
              defaultValue=""
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onFocus={() => {
                // Scroll to bottom when focused to show latest messages above keyboard
                setTimeout(() => scrollToBottom(true), 300);
                // Scroll again after keyboard fully opens
                setTimeout(() => scrollToBottom(true), 500);
              }}
              placeholder={t('chat.placeholder', { coach: profile?.ai_coach_name || 'Arnold' })}
              className="flex-1 min-w-0 bg-background/50 border-border/50 focus:border-primary resize-none min-h-[60px] sm:min-h-[80px] max-h-[200px] text-sm sm:text-base mr-1"
              rows={3}
            />
            <Button
              onClick={sendMessage}
              disabled={!canSend || isLoading}
              aria-label={t('chat.send', { defaultValue: 'Send message' })}
              className="shrink-0 bg-gradient-primary text-black border-0 shadow-glow hover:shadow-lg self-center h-11 w-11 p-0 rounded-full flex items-center justify-center"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

      <WorkoutImportModal
        isOpen={showWorkoutModal}
        onClose={() => setShowWorkoutModal(false)}
        onWorkoutSelect={handleWorkoutImport}
      />

      <WorkoutProgramReviewModal
        isOpen={showProgramModal}
        onClose={() => {
          setShowProgramModal(false);
          setSelectedProgramData(null);
        }}
        programData={selectedProgramData}
        onImport={importWorkoutProgram}
        isImporting={isImportingProgram}
      />

      <RecipeReviewModal
        isOpen={showRecipeModal}
        recipeData={selectedRecipes[currentRecipeIndex] || null}
        currentIndex={currentRecipeIndex}
        totalRecipes={selectedRecipes.length}
        onClose={() => {
          Analytics.arnoldSuggestionDismissed('recipe');
          setShowRecipeModal(false);
          setSelectedRecipes([]);
          setCurrentRecipeIndex(0);
        }}
        onSkip={() => {
          // Skip current recipe and move to next
          if (currentRecipeIndex < selectedRecipes.length - 1) {
            setCurrentRecipeIndex(prev => prev + 1);
            toast.info(t('chat.toasts.recipeSkipped'));
          } else {
            setShowRecipeModal(false);
            setSelectedRecipes([]);
            setCurrentRecipeIndex(0);
            toast.info(t('chat.toasts.recipeReviewCompleted'));
          }
        }}
        onImport={async () => {
          if (selectedRecipes.length === 0 || currentRecipeIndex >= selectedRecipes.length) return;
          
          const recipe = selectedRecipes[currentRecipeIndex];
          setIsImportingRecipe(true);
          
          try {
            await createRecipe(
              {
                title: recipe.title,
                description: recipe.description,
                servings: recipe.servings,
                prep_time_minutes: recipe.prep_time_minutes,
                cook_time_minutes: recipe.cook_time_minutes,
                instructions: recipe.instructions,
                calories: recipe.calories,
                protein: recipe.protein,
                carbs: recipe.carbs,
                fat: recipe.fat,
                fiber: recipe.fiber,
                sugar: recipe.sugar,
                sodium: recipe.sodium,
                equipment: recipe.equipment
              },
              recipe.ingredients
            );
            
            // If there are more recipes, show the next one
            if (currentRecipeIndex < selectedRecipes.length - 1) {
              setCurrentRecipeIndex(prev => prev + 1);
            } else {
              // All recipes imported, close modal
              Analytics.arnoldRecipeSaved();
              setShowRecipeModal(false);
              setSelectedRecipes([]);
              setCurrentRecipeIndex(0);
              toast.success(t('chat.toasts.recipesSaved'));
            }
          } catch (error) {
            console.error('Error importing recipe:', error);
            toast.error(t('chat.toasts.recipeSaveFailed'));
          } finally {
            setIsImportingRecipe(false);
          }
        }}
        isImporting={isImportingRecipe}
      />
    </div>
  );
};